import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLocations } from "../../actions/EmpDiscripencyAction";
import RosterAttendanceService from "../../service/RosterAttendanceService";
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  useTheme, 
  useMediaQuery,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Backdrop,
  LinearProgress,
  IconButton,
  Tooltip,
  Menu,
  TextField,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LanguageIcon from "@mui/icons-material/Language";
import TranslateIcon from "@mui/icons-material/Translate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ------------ Translation Texts ------------
const translations = {
  en: {
    title: "Roster Absent List",
    datePicker: "Select Date",
    exportPdf: "Export PDF",
    exporting: "Exporting...",
    totalStrength: "Total Strength",
    attendance: "Attendance",
    absent: "Absent",
    location: "Location",
    strength: "Strength",
    loading: "Loading data...",
    noData: "No attendance data available for the selected date",
    noAbsent: "No absent employees found for the selected date",
    total: "Total",
    reportTitle: "Roster Absent Report",
    reportDate: "Report Date",
    page: "Page",
    of: "of",
    generatedOn: "Generated on",
    pdfSuccess: "PDF exported successfully!",
    pdfError: "Failed to export PDF. Please try again.",
    language: "Language",
    totalEmp: "Total Employees",
    present: "Present",
    attendanceRate: "Attendance Rate (%)",
    generatingReport: "Generating roster absent report...",
    allLocations: "All Locations",
    selectLocation: "Select Location",
    sno: "S.No",
    name: "Name",
    department: "Department",
    loadingLocations: "Loading locations...",
    backToSummary: "Back to Summary",
    totalAbsent: "Total Absent",
    prepareReport: "Preparing your report...",
    generatingSummary: "Generating summary report...",
    generatingAbsent: "Generating absent employee report...",
    rosterType: "Type",
    roster: "Roster",
    nonRoster: "Non-Roster",
    rosterAssigned: "Roster Assigned",
    shiftMismatch: "Shift Mismatch",
    rosterLocations: "Roster Locations",
    nonRosterLocations: "Non-Roster Locations",
  },
  si: {
    title: "පැමිණ නොමැති ලැයිස්තුව (රෝස්ටර්)",
    datePicker: "දිනය තෝරන්න",
    exportPdf: "PDF බාගත කරන්න",
    exporting: "PDF බාගත කරමින්...",
    totalStrength: "මුළු සේවක පිරිස",
    attendance: "පැමිණි සේවකයන්",
    absent: "පැමිණ නොමැති සේවකයන්",
    location: "ස්ථානය",
    strength: "ශක්තිය",
    loading: "දත්ත පූරණය වෙමින්...",
    noData: "තෝරාගත් දිනය සඳහා පැමිණීම් දත්ත නොමැත",
    noAbsent: "තෝරාගත් දිනය සඳහා පැමිණ නොමැති සේවකයන් නොමැත",
    total: "මුළු",
    reportTitle: "රෝස්ටර් පැමිණ නොමැති වාර්තාව",
    reportDate: "වාර්තා දිනය",
    page: "පිටුව",
    of: "හි",
    generatedOn: "ජනනය කරන ලද දිනය",
    pdfSuccess: "PDF සාර්ථකව බාගත කරන ලදී!",
    pdfError: "PDF බාගත කිරීමට නොහැකි විය. නැවත උත්සාහ කරන්න.",
    language: "භාෂාව",
    totalEmp: "මුළු සේවකයන්",
    present: "පැමිණි",
    attendanceRate: "පැමිණීම් අනුපාතය (%)",
    generatingReport: "රෝස්ටර් පැමිණ නොමැති වාර්තාව ජනනය කරමින්...",
    allLocations: "සියලුම ස්ථාන",
    selectLocation: "ස්ථානය තෝරන්න",
    sno: "අංකය",
    name: "නම",
    department: "දෙපාර්තමේන්තුව",
    loadingLocations: "ස්ථාන පූරණය වෙමින්...",
    backToSummary: "සාරාංශයට ආපසු යන්න",
    totalAbsent: "මුළු පැමිණ නොමැති සේවකයන්",
    prepareReport: "ඔබේ වාර්තාව සකස් කරමින්...",
    generatingSummary: "සාරාංශ වාර්තාව ජනනය කරමින්...",
    generatingAbsent: "පැමිණ නොමැති සේවක වාර්තාව ජනනය කරමින්...",
    rosterType: "වර්ගය",
    roster: "රෝස්ටර්",
    nonRoster: "රෝස්ටර් නොවන",
    rosterAssigned: "රෝස්ටර් පවරා ඇත",
    shiftMismatch: "මාරුව නොගැලපීම",
    rosterLocations: "රෝස්ටර් ස්ථාන",
    nonRosterLocations: "රෝස්ටර් නොවන ස්ථාන",
  },
};

// ------------ Styles ------------
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "8px 12px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    padding: "0px 8px",
  },
}));

const StickyCardsContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "0px",
  paddingBottom: "4px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("sm")]: {
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto",
  },
}));

// Table Styles matching the provided design
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "10px",
  boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
  border: "1px solid #d1d9e6",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  [theme.breakpoints.up("sm")]: {
    maxHeight: "500px",
    overflowY: "auto",
    overflowX: "auto",
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: "500px",
    overflowY: "auto",
    overflowX: "auto",
  },
}));

const EmployeeCard = styled(Box)(({ theme }) => ({
  padding: "10px 12px",
  borderRadius: "10px",
  marginBottom: "8px",
  boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.07)",
  backgroundColor: "#fff",
  border: "1px solid #d1d9e6",
  transition: "0.15s ease",
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.06)",
    cursor: "pointer",
    transform: "translateY(-1px)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 10px",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "8px 6px",
  fontSize: "13px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.3px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    "&:first-of-type": {
      borderTopLeftRadius: "10px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "10px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 4px",
    fontSize: "11px",
    "&.MuiTableCell-head": {
      fontSize: "12px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.15s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.06)",
    cursor: "pointer",
  },
  "&:last-of-type td:first-of-type": {
    borderBottomLeftRadius: "10px",
  },
  "&:last-of-type td:last-of-type": {
    borderBottomRightRadius: "10px",
  },
}));

const RosterAbsentList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { locationsData, locationsLoading } = useSelector((state) => state.empDiscripency);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLocation, setSelectedLocation] = useState("");
  const [language, setLanguage] = useState("en");
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [rosterSummaryData, setRosterSummaryData] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [fullAbsentData, setFullAbsentData] = useState([]);
  const [loadingAbsent, setLoadingAbsent] = useState(false);
  const [viewMode, setViewMode] = useState("summary");
  const [rosterTypeFilter, setRosterTypeFilter] = useState("all"); // "all", "roster", "nonRoster"
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState("");
  const [selectedLocationCode, setSelectedLocationCode] = useState("");

  const t = translations[language];

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  // Helper function to get roster type display label and colors (UPDATED)
  const getRosterTypeDisplay = (rosterValue) => {
    const roster = (rosterValue || "").trim();
    
    if (roster === "N") {
      return {
        label: t.nonRoster || "Non-Roster",
        backgroundColor: "rgba(237, 108, 2, 0.1)",
        color: "#ed6c02", // Orange
      };
    } else if (roster === "A - Scheduled") {
      return {
        label: t.rosterAssigned || "Roster Assigned",
        backgroundColor: "rgba(25, 118, 210, 0.1)",
        color: "#1976d2", // Blue
      };
    } else if (roster === "A - Not Scheduled") {
      return {
        label: t.shiftMismatch || "Shift Mismatch",
        backgroundColor: "rgba(156, 39, 176, 0.1)",
        color: "#9c27b0", // Purple
      };
    }
    
    // Default fallback
    return {
      label: roster || "Unknown",
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#9e9e9e", // Gray
    };
  };

  useEffect(() => {
    const fetchFullAbsent = async () => {
      setLoadingAbsent(true);
      try {
        const data = await RosterAttendanceService.getFullAbsentDetailsForRoster(formatDate(selectedDate));
        setFullAbsentData(data || []);
      } catch (error) {
        console.error("Error fetching full absent data:", error);
        setFullAbsentData([]);
      } finally {
        setLoadingAbsent(false);
      }
    };
    fetchFullAbsent();
  }, [selectedDate]);

  useEffect(() => {
    // Fetch roster attendance summary for cards with roster type
    const fetchRosterSummary = async () => {
      try {
        const data = await RosterAttendanceService.getByDate(formatDate(selectedDate));
        setRosterSummaryData(data || []);
      } catch (error) {
        console.error("Error fetching roster summary:", error);
        setRosterSummaryData([]);
      }
    };
    fetchRosterSummary();
  }, [selectedDate]);

  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  // Filter data by roster type and group by location + type (uses new API data)
  const filteredRosterSummaryData = useMemo(() => {
    if (!fullAbsentData || !Array.isArray(fullAbsentData)) return [];
    
    // Group by location name AND roster type to keep them separate, SUM duplicates
    const groupedByLocationAndType = {};
    fullAbsentData.forEach(item => {
      if (item.location !== "TOTAL") {
        const key = `${item.location}__${item.Roster}`;
        if (!groupedByLocationAndType[key]) {
          groupedByLocationAndType[key] = {
            location: item.location,
            TotalEmp: Number(item.TotalEmp || 0),
            TodayAbesnt: Number(item.TodayAbesnt || 0),
            Roster: item.Roster,
            rosterDisplay: getRosterTypeDisplay(item.Roster),
          };
        } else {
          groupedByLocationAndType[key].TotalEmp += Number(item.TotalEmp || 0);
          groupedByLocationAndType[key].TodayAbesnt += Number(item.TodayAbesnt || 0);
        }
      }
    });

    let filtered = Object.values(groupedByLocationAndType).map(item => {
      // Recalculate absent percentage after aggregation
      const total = Number(item.TotalEmp || 0);
      const absent = Number(item.TodayAbesnt || 0);
      return {
        ...item,
        Percentage: total > 0 ? Math.round((absent / total) * 100) : 0,
      };
    });

    if (rosterTypeFilter === "roster") {
      filtered = filtered.filter(item => item.Roster && item.Roster.startsWith("A"));
    } else if (rosterTypeFilter === "nonRoster") {
      filtered = filtered.filter(item => item.Roster === "N");
    }
    
    return filtered.sort((a, b) => {
      const absentA = parseInt(a.TodayAbesnt || 0);
      const absentB = parseInt(b.TodayAbesnt || 0);
      return absentB - absentA;
    });
  }, [fullAbsentData, rosterTypeFilter]);

  const filteredAbsentData = useMemo(() => {
    // Map the API response data directly
    const mappedData = fullAbsentData.map((item) => {
      // Get the roster type display based on the Roster field from API
      const rosterDisplay = getRosterTypeDisplay(item.Roster);
      
      return {
        ...item,
        locationName: item.location,
        departmentName: item.department || item.depatmentCode,
        rosterType: item.Roster,
        rosterDisplay: rosterDisplay,
      };
    });

    // Apply location filter
    let filtered = mappedData;
    if (selectedLocation) {
      filtered = filtered.filter((item) => item.location === selectedLocation);
    }
    
    // Apply roster type filter
    if (rosterTypeFilter === "roster") {
      filtered = filtered.filter(item => item.Roster && item.Roster.startsWith("A"));
    } else if (rosterTypeFilter === "nonRoster") {
      filtered = filtered.filter(item => item.Roster === "N");
    }
    
    return filtered;
  }, [fullAbsentData, selectedLocation, rosterTypeFilter]);

  const summaryStats = useMemo(() => {
    if (!fullAbsentData || !Array.isArray(fullAbsentData) || fullAbsentData.length === 0) {
      return { 
        totalStrength: 0, 
        absentCount: 0, 
        presentCount: 0, 
        percentage: 0, 
        currentLocation: 'All',
        rosterCount: 0,
        nonRosterCount: 0,
        rosterAssignedCount: 0,
        shiftMismatchCount: 0,
      };
    }

    // Group by location + Roster type, SUM duplicates
    const groupedByLocationAndType = {};
    fullAbsentData.forEach(item => {
      if (item.location !== "TOTAL") {
        const key = `${item.location}__${item.Roster}`;
        if (!groupedByLocationAndType[key]) {
          groupedByLocationAndType[key] = {
            location: item.location,
            TotalEmp: Number(item.TotalEmp || 0),
            TodayAbesnt: Number(item.TodayAbesnt || 0),
            Roster: item.Roster,
          };
        } else {
          groupedByLocationAndType[key].TotalEmp += Number(item.TotalEmp || 0);
          groupedByLocationAndType[key].TodayAbesnt += Number(item.TodayAbesnt || 0);
        }
      }
    });

    const groupedData = Object.values(groupedByLocationAndType);

    // Filter by selected location
    let filteredSummary = selectedLocation
      ? groupedData.filter((item) => item.location === selectedLocation)
      : groupedData;

    // Apply roster type filter
    if (rosterTypeFilter === "roster") {
      filteredSummary = filteredSummary.filter(item => item.Roster && item.Roster.startsWith("A"));
    } else if (rosterTypeFilter === "nonRoster") {
      filteredSummary = filteredSummary.filter(item => item.Roster === "N");
    }

    const totalStrength = filteredSummary.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
    const absentCount = filteredSummary.reduce((sum, item) => sum + Number(item.TodayAbesnt || 0), 0);
    const presentCount = totalStrength - absentCount;
    const percentage = totalStrength > 0 ? Math.round((absentCount / totalStrength) * 100) : 0;

    // Count roster types
    const rosterItems = filteredSummary.filter(item => item.Roster && item.Roster.startsWith("A"));
    const nonRosterItems = filteredSummary.filter(item => item.Roster === "N");
    const rosterAssignedItems = filteredSummary.filter(item => item.Roster === "A - Scheduled");
    const shiftMismatchItems = filteredSummary.filter(item => item.Roster === "A - Not Scheduled");

    const currentLocation = selectedLocation || 'All';

    return { 
      totalStrength, 
      presentCount, 
      absentCount, 
      percentage, 
      currentLocation,
      rosterCount: rosterItems.length,
      nonRosterCount: nonRosterItems.length,
      rosterAssignedCount: rosterAssignedItems.length,
      shiftMismatchCount: shiftMismatchItems.length,
    };
  }, [fullAbsentData, selectedLocation, rosterTypeFilter]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleLanguageMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setOpenDatePicker(false);
    setViewMode("summary");
    setSelectedLocation("");
    setSelectedLocationCode("");
    setRosterTypeFilter("all");
    setEmployees([]);
    setSelectedRoster("");
  };

  const handleLocationChange = (event) => {
    const value = event.target.value;
    setSelectedLocationCode(value);
    
    // Also set the location name for display
    const locationObj = locationsData?.find((loc) => loc.locCode === value);
    setSelectedLocation(locationObj ? locationObj.locName : value);
    
    if (value === "") {
      setViewMode("summary");
      setEmployees([]);
    } else {
      setViewMode("details");
    }
  };

  const handleRosterTypeFilterChange = (event) => {
    setRosterTypeFilter(event.target.value);
  };

  const handleLocationCardClick = async (locationName, rosterType) => {
    if (locationName) {
      // Find the location CODE from locationsData (API needs code, not name)
      const locationObj = locationsData?.find(
        (loc) => loc.locName === locationName || loc.locCode === locationName
      );
      const locationCode = locationObj ? locationObj.locCode : locationName;
      
      console.log("=== CLICK ===");
      console.log("Location:", locationName, "→ Code:", locationCode, "| Type:", rosterType);
      
      setSelectedLocation(locationName);
      setSelectedRoster(rosterType);
      setViewMode("details");
      setLoadingEmployees(true);
      setEmployees([]);
      
      try {
        // Use the absent-only endpoint (GetAbsentDetailsForDaywithL)
        const absentEmployees = await RosterAttendanceService.getAbsentEmployeesByLocation(
          formatDate(selectedDate),
          locationCode
        );
        
        console.log("Absent employees for", locationCode, ":", absentEmployees.length);
        
        if (absentEmployees.length > 0) {
          console.log("Fields:", Object.keys(absentEmployees[0]));
          console.log("First:", JSON.stringify(absentEmployees[0]));
        }
        
        // These are already ABSENT employees only.
        // Now set them — the API returns absent employees for this location.
        setEmployees(absentEmployees);
      } catch (error) {
        console.error("Error fetching absent employees:", error);
        setSnackbar({
          open: true,
          message: "Failed to load absent employee details",
          severity: "error",
        });
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    }
  };

  const handleBackToSummary = () => {
    setViewMode("summary");
    setSelectedLocation("");
    setSelectedLocationCode("");
    setRosterTypeFilter("all");
    setEmployees([]);
    setSelectedRoster("");
  };

  const handleExportPDF = async () => {
    if (exporting) return;
    
    try {
      setExporting(true);
      setExportProgress(10);
      
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      setExportProgress(20);

      // Title based on view mode
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      
      if (viewMode === "summary") {
        doc.text("Roster Attendance Summary Report", pageWidth / 2, 20, { align: "center" });
      } else {
        doc.text("Roster Absent Report", pageWidth / 2, 20, { align: "center" });
      }

      setExportProgress(30);

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Report Date: ${formatDate(selectedDate)}`, 20, 35);
      
      if (viewMode === "summary") {
        doc.text(`Total Employees: ${summaryStats.totalStrength}`, 20, 42);
        doc.text(`Present: ${summaryStats.presentCount}`, 20, 49);
        doc.text(`Absent: ${summaryStats.absentCount}`, 20, 56);
        doc.text(`Roster Assigned Locations: ${summaryStats.rosterAssignedCount}`, 20, 63);
        doc.text(`Shift Mismatch Locations: ${summaryStats.shiftMismatchCount}`, 20, 70);
        doc.text(`Non-Roster Locations: ${summaryStats.nonRosterCount}`, 20, 77);
      } else {
        doc.text(`Location: ${selectedLocation}`, 20, 42);
        doc.text(`Total Absent: ${employees.length > 0 ? employees.length : filteredAbsentData.length}`, 20, 49);
      }
      
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, viewMode === "summary" ? 84 : 56);

      setExportProgress(50);

      if (viewMode === "summary") {
        // Summary table with roster type
        autoTable(doc, {
          startY: 95,
          head: [["Location", "Type", "Strength", "Present", "Absent", "Attendance %"]],
          body: filteredRosterSummaryData?.map((item) => {
            const total = parseInt(item.TotalEmp || 0);
            const absent = parseInt(item.TodayAbesnt || 0);
            const present = total - absent;
            const percentage = item.Percentage || (total > 0 ? Math.round((present / total) * 100) : 0);
            const rosterLabel = getRosterTypeDisplay(item.Roster).label;
            
            return [
              item.location || "N/A",
              rosterLabel,
              total.toString(),
              present.toString(),
              absent.toString(),
              `${percentage}%`,
            ];
          }),
          styles: {
            fontSize: 9,
            cellPadding: 4,
          },
          headStyles: {
            fillColor: [21, 76, 121],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 248, 252],
          },
          foot: [
            [
              "TOTAL",
              `RA:${summaryStats.rosterAssignedCount}/SM:${summaryStats.shiftMismatchCount}/NR:${summaryStats.nonRosterCount}`,
              summaryStats.totalStrength.toString(),
              summaryStats.presentCount.toString(),
              summaryStats.absentCount.toString(),
              `${summaryStats.totalStrength > 0 ? Math.round((summaryStats.presentCount / summaryStats.totalStrength) * 100) : 0}%`,
            ],
          ],
          footStyles: {
            fillColor: [21, 76, 121],
            textColor: 255,
            fontStyle: "bold",
          },
        });
      } else if (employees.length > 0) {
        // Employee-level absent details table
        autoTable(doc, {
          startY: 65,
          head: [["No", "Name", "Department", "Type"]],
          body: employees?.map((emp, index) => {
            const empRoster = emp.Roster || emp.roster || emp.rosterType || selectedRoster;
            const rosterLabel = getRosterTypeDisplay(empRoster).label;
            return [
              (index + 1).toString(),
              emp.name || emp.empName || emp.employeeName || emp.Name || "N/A",
              emp.department || emp.departmentName || emp.Department || emp.depatmentCode || "N/A",
              rosterLabel,
            ];
          }),
          styles: {
            fontSize: 9,
            cellPadding: 4,
          },
          headStyles: {
            fillColor: [21, 76, 121],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 248, 252],
          },
          foot: [
            [
              "",
              "Total Absent:",
              employees.length.toString(),
              "",
            ],
          ],
          footStyles: {
            fillColor: [21, 76, 121],
            textColor: 255,
            fontStyle: "bold",
          },
        });
      } else {
        // Absent details table with roster type (location aggregates)
        autoTable(doc, {
          startY: 65,
          head: [["No", "Location", "Strength", "Absent", "Attendance %", "Type"]],
          body: filteredAbsentData?.map((item, index) => {
            const total = parseInt(item.TotalEmp || 0);
            const absent = parseInt(item.TodayAbesnt || 0);
            const rosterLabel = getRosterTypeDisplay(item.Roster).label;
            return [
              (index + 1).toString(),
              item.location || "N/A",
              total.toString(),
              absent.toString(),
              `${item.Percentage || 0}%`,
              rosterLabel,
            ];
          }),
          styles: {
            fontSize: 9,
            cellPadding: 4,
          },
          headStyles: {
            fillColor: [21, 76, 121],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 248, 252],
          },
          foot: [
            [
              "",
              "",
              "Total Absent:",
              filteredAbsentData.length.toString(),
              "",
              "",
            ],
          ],
          footStyles: {
            fillColor: [21, 76, 121],
            textColor: 255,
            fontStyle: "bold",
          },
        });
      }

      setExportProgress(80);

      // Add footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - 20,
          pageHeight - 10,
          { align: "right" }
        );
      }

      setExportProgress(100);

      // Save the PDF
      const filename = viewMode === "summary"
        ? `Roster_Summary_${formatDate(selectedDate)}.pdf`
        : `Roster_Absent_${formatDate(selectedDate)}.pdf`;
      
      doc.save(filename);
      
      showSnackbar("PDF exported successfully!", "success");
      
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error("PDF export error:", error);
      showSnackbar("Failed to export PDF. Please try again.", "error");
      setExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Export Progress Backdrop */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          open={exporting}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 4,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              maxWidth: 400,
              width: "100%",
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{ color: "#154c79" }}
            />
            <Typography variant="h6" color="primary" fontWeight="bold">
              {viewMode === "summary" 
                ? t.generatingSummary
                : t.generatingAbsent}
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={exportProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: viewMode === "summary" ? "#154c79" : "#2e7d32",
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={1}
              >
                {exportProgress}% {language === 'si' ? "සම්පූර්ණ" : "Complete"}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              {t.prepareReport}
            </Typography>
          </Box>
        </Backdrop>

        {/* Header with Language Selector */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#0e3c66",
              textAlign: "center",
              flex: 1,
            }}
          >
            {t.title}
          </Typography>
          
          {/* Language Selector */}
          <Tooltip title={t.language}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              sx={{
                backgroundColor: language === 'si' ? '#154c79' : '#f0f0f0',
                color: language === 'si' ? 'white' : '#154c79',
                '&:hover': {
                  backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
                },
                width: '36px',
                height: '36px',
                ml: 1,
              }}
            >
              {language === 'si' ? (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
              ) : (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
              )}
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            }}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
              sx={{
                backgroundColor: language === 'en' ? 'rgba(21, 76, 121, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(21, 76, 121, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon sx={{ fontSize: 18, color: '#154c79' }} />
                <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
                  English
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('si')}
              selected={language === 'si'}
              sx={{
                backgroundColor: language === 'si' ? 'rgba(21, 76, 121, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(21, 76, 121, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TranslateIcon sx={{ fontSize: 18, color: '#154c79' }} />
                <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
                  සිංහල
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Box>

        {/* Date Picker, Location Dropdown, Type Filter, and Export Button */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1.5,
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setOpenDatePicker(true)}
            disabled={exporting}
            sx={{
              flex: isMobile ? 1 : 1.5,
              minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting ? "#9e9e9e" : "#154c79",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { 
                backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
              },
            }}
          >
            {formatDate(selectedDate)}
          </Button>

          <FormControl
            sx={{
              flex: isMobile ? 1 : 1.5,
              minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
            }}
            disabled={locationsLoading}
          >
            <Select
              value={selectedLocationCode}
              onChange={handleLocationChange}
              displayEmpty
              disabled={exporting || locationsLoading}
              sx={{
                height: "38px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                backgroundColor: exporting ? "#f5f5f5" : "#ffffff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: exporting ? "#e0e0e0" : undefined,
                },
              }}
            >
              <MenuItem value="">
                <em>{t.allLocations}</em>
              </MenuItem>
              {locationsLoading ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">{t.loadingLocations}</Typography>
                  </Box>
                </MenuItem>
              ) : (
                locationsData && locationsData.map((location) => (
                  <MenuItem key={location.locCode} value={location.locCode}>
                    {location.locName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Roster Type Filter Dropdown (UPDATED) */}
          <FormControl
            sx={{
              flex: isMobile ? 1 : 1.5,
              minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
            }}
          >
            <Select
              value={rosterTypeFilter}
              onChange={handleRosterTypeFilterChange}
              displayEmpty
              disabled={exporting}
              sx={{
                height: "38px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                backgroundColor: exporting ? "#f5f5f5" : "#ffffff",
              }}
            >
              <MenuItem value="all">
                <em>All Types</em>
              </MenuItem>
              <MenuItem value="roster">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#1976d2', borderRadius: '2px' }} />
                  Roster
                </Box>
              </MenuItem>
              <MenuItem value="nonRoster">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#ed6c02', borderRadius: '2px' }} />
                  Non-Roster
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={exporting || (viewMode === "details" && employees.length === 0 && (!filteredAbsentData || filteredAbsentData.length === 0))}
            sx={{
              flex: isMobile ? 1 : 1,
              minWidth: isMobile ? "100%" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting 
                ? "#9e9e9e" 
                : (viewMode === "details" && employees.length === 0 && (!filteredAbsentData || filteredAbsentData.length === 0) 
                  ? "#bdbdbd" 
                  : viewMode === "summary" 
                  ? "#097721" 
                  : "#2e7d32"),
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { 
                backgroundColor: exporting 
                  ? "#9e9e9e" 
                  : (viewMode === "details" && employees.length === 0 && (!filteredAbsentData || filteredAbsentData.length === 0) 
                    ? "#bdbdbd" 
                    : viewMode === "summary" 
                    ? "#0D47A1" 
                    : "#1b5e20") 
              },
            }}
          >
            {exporting ? t.exporting : t.exportPdf}
          </Button>
        </Box>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            onClose={() => setOpenDatePicker(false)}
            value={selectedDate}
            onChange={handleDateChange}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Sticky Stats Cards */}
        {fullAbsentData && fullAbsentData.length > 0 && (
          <StickyCardsContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "6px",
                mb: 1,
              }}
            >
              {/* Total Strength Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
                  border: "1px solid #aec4e0",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#0E3C66",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.totalStrength}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {summaryStats.totalStrength}
                </Typography>
              </Box>

              {/* Attendance Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
                  border: "1px solid #80cbc4",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#004d40",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.attendance}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {summaryStats.presentCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "9px" : "10px",
                    fontWeight: 700,
                    color:
                      summaryStats.totalStrength > 0 
                        ? (summaryStats.presentCount / summaryStats.totalStrength) > 0.7
                          ? "#2E7D32"
                          : (summaryStats.presentCount / summaryStats.totalStrength) > 0.5
                          ? "#FF9800"
                          : "#D32F2F"
                        : "#2E7D32",
                  }}
                >
                  ({summaryStats.totalStrength > 0 ? Math.round((summaryStats.presentCount / summaryStats.totalStrength) * 100) : 0}%)
                </Typography>
              </Box>

              {/* Absent Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                  border: "1px solid #ef9a9a",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#b71c1c",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.absent}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {summaryStats.absentCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "9px" : "10px",
                    fontWeight: 700,
                    color: "#D32F2F",
                  }}
                >
                  ({summaryStats.percentage}%)
                </Typography>
              </Box>
            </Box>
          </StickyCardsContainer>
        )}

        <ScrollableContent>
          {viewMode === "summary" ? (
            // Summary View - TABLE with all locations
            filteredRosterSummaryData && filteredRosterSummaryData.length > 0 ? (
              <StyledTableContainer component={Paper} sx={{ mt: 2 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>{t.location}</StyledTableCell>
                      <StyledTableCell>{t.rosterType}</StyledTableCell>
                      <StyledTableCell>{t.strength}</StyledTableCell>
                      <StyledTableCell>{t.present}</StyledTableCell>
                      <StyledTableCell>{t.absent}</StyledTableCell>
                      <StyledTableCell>{t.attendanceRate}</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRosterSummaryData.map((row, index) => {
                      const total = parseInt(row.TotalEmp || 0);
                      const absent = parseInt(row.TodayAbesnt || 0);
                      const present = total - absent;
                      const percentage = row.Percentage || (total > 0 ? Math.round((present / total) * 100) : 0);
                      
                      return (
                        <StyledTableRow 
                          key={index}
                          onClick={() => handleLocationCardClick(row.location, row.Roster)}
                        >
                          <StyledTableCell
                            sx={{
                              textAlign: "left",
                              pl: isMobile ? 2 : 3,
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.location}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={row.rosterDisplay?.label || "Unknown"}
                              size="small"
                              sx={{
                                backgroundColor: row.rosterDisplay?.backgroundColor || "rgba(158, 158, 158, 0.1)",
                                color: row.rosterDisplay?.color || "#9e9e9e",
                                fontWeight: 600,
                                fontSize: "11px",
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>{total}</StyledTableCell>
                          <StyledTableCell>{present}</StyledTableCell>
                          <StyledTableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: absent > 0 ? "#D32F2F" : "#2E7D32",
                                fontSize: "13px",
                              }}
                            >
                              {absent}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: percentage > 70 ? "#2E7D32" : percentage > 50 ? "#FF9800" : "#D32F2F",
                              }}
                            >
                              {percentage}%
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                    
                    {/* Total Row (UPDATED) */}
                    <StyledTableRow
                      sx={{
                        backgroundColor: "#154c79 !important",
                        "&:hover": { backgroundColor: "#154c79 !important" },
                        position: "sticky",
                        bottom: 0,
                        zIndex: 5,
                      }}
                    >
                      <StyledTableCell
                        sx={{
                          color: "#ffffff",
                          fontWeight: 700,
                          textAlign: "left",
                          pl: isMobile ? 2 : 3,
                        }}
                      >
                        {t.total}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        RA:{summaryStats.rosterAssignedCount}/SM:{summaryStats.shiftMismatchCount}/NR:{summaryStats.nonRosterCount}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {summaryStats.totalStrength}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {summaryStats.presentCount}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {summaryStats.absentCount}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {summaryStats.totalStrength > 0 ? Math.round((summaryStats.presentCount / summaryStats.totalStrength) * 100) : 0}%
                      </StyledTableCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </StyledTableContainer>
            ) : (
              <Typography
                variant="body1"
                sx={{ textAlign: "center", padding: "40px", color: "#666", fontStyle: "italic" }}
              >
                {t.noData}
              </Typography>
            )
          ) : (
            // Details View - Absent Employees Table
            <>
              {viewMode === "details" && (
                <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToSummary}
                    sx={{
                      textTransform: "none",
                      fontWeight: "bold",
                      color: "#154c79",
                      fontSize: "13px",
                    }}
                  >
                    {t.backToSummary}
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color: "#D32F2F",
                      fontSize: "13px",
                    }}
                  >
                    {t.totalAbsent}: {employees.length > 0 ? employees.length : filteredAbsentData.length}
                  </Typography>
                </Box>
              )}

              {loadingAbsent || loadingEmployees ? (
                <Box sx={{ textAlign: "center", padding: "40px" }}>
                  <CircularProgress sx={{ color: "#154c79" }} />
                  <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
                    {t.loading}
                  </Typography>
                </Box>
              ) : employees.length > 0 ? (
                employees.map((emp, index) => (
                  <EmployeeCard key={emp.empCode || emp.sno || emp.id || index}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {/* Employee Image */}
                      <img
                        src={`/api/placeholder/50/50`}
                        alt={emp.name || emp.empName || "Employee"}
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #154c79",
                        }}
                        onError={(e) => (e.target.src = "/placeholder-image.jpg")}
                      />

                      {/* Employee Details */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: "#154c79", display: "block", fontSize: "11px" }}
                        >
                          {emp.sno || emp.empCode || ""}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          sx={{ fontSize: "14px" }}
                        >
                          {emp.name || emp.empName || emp.employeeName || emp.Name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#666", display: "block", fontSize: "11px" }}
                        >
                          {selectedLocation || emp.locationCode || emp.locationName || ""}
                        </Typography>
                      </Box>
                    </Box>
                  </EmployeeCard>
                ))
              ) : (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", padding: "40px", color: "#666", fontStyle: "italic" }}
                >
                  {t.noAbsent}
                </Typography>
              )}
            </>
          )}
        </ScrollableContent>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PageContainer>
    </LocalizationProvider>
  );
};

export default RosterAbsentList;


// import React, { useEffect, useState, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchLocations } from "../../actions/EmpDiscripencyAction";
// import RosterAttendanceService from "../../service/RosterAttendanceService";
// import { 
//   Box, 
//   CircularProgress, 
//   Typography, 
//   Paper, 
//   Table, 
//   TableBody, 
//   TableCell, 
//   TableContainer, 
//   TableHead, 
//   TableRow, 
//   useTheme, 
//   useMediaQuery,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Snackbar,
//   Alert,
//   Backdrop,
//   LinearProgress,
//   IconButton,
//   Tooltip,
//   Menu,
//   TextField,
//   Chip,
// } from "@mui/material";
// import { styled } from "@mui/material/styles";
// import { format } from "date-fns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import LanguageIcon from "@mui/icons-material/Language";
// import TranslateIcon from "@mui/icons-material/Translate";
// import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Roster Absent List",
//     datePicker: "Select Date",
//     exportPdf: "Export PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     attendance: "Attendance",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     loading: "Loading data...",
//     noData: "No attendance data available for the selected date",
//     noAbsent: "No absent employees found for the selected date",
//     total: "Total",
//     reportTitle: "Roster Absent Report",
//     reportDate: "Report Date",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     language: "Language",
//     totalEmp: "Total Employees",
//     present: "Present",
//     attendanceRate: "Attendance Rate (%)",
//     generatingReport: "Generating roster absent report...",
//     allLocations: "All Locations",
//     selectLocation: "Select Location",
//     sno: "S.No",
//     name: "Name",
//     department: "Department",
//     loadingLocations: "Loading locations...",
//     backToSummary: "Back to Summary",
//     totalAbsent: "Total Absent",
//     prepareReport: "Preparing your report...",
//     generatingSummary: "Generating summary report...",
//     generatingAbsent: "Generating absent employee report...",
//     rosterType: "Type",
//     roster: "Roster",
//     common: "Common",
//     rosterLocations: "Roster Locations",
//     commonLocations: "Common Locations",
//   },
//   si: {
//     title: "පැමිණ නොමැති ලැයිස්තුව (රෝස්ටර්)",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     attendance: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "ශක්තිය",
//     loading: "දත්ත පූරණය වෙමින්...",
//     noData: "තෝරාගත් දිනය සඳහා පැමිණීම් දත්ත නොමැත",
//     noAbsent: "තෝරාගත් දිනය සඳහා පැමිණ නොමැති සේවකයන් නොමැත",
//     total: "මුළු",
//     reportTitle: "රෝස්ටර් පැමිණ නොමැති වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     page: "පිටුව",
//     of: "හි",
//     generatedOn: "ජනනය කරන ලද දිනය",
//     pdfSuccess: "PDF සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF බාගත කිරීමට නොහැකි විය. නැවත උත්සාහ කරන්න.",
//     language: "භාෂාව",
//     totalEmp: "මුළු සේවකයන්",
//     present: "පැමිණි",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     generatingReport: "රෝස්ටර් පැමිණ නොමැති වාර්තාව ජනනය කරමින්...",
//     allLocations: "සියලුම ස්ථාන",
//     selectLocation: "ස්ථානය තෝරන්න",
//     sno: "අංකය",
//     name: "නම",
//     department: "දෙපාර්තමේන්තුව",
//     loadingLocations: "ස්ථාන පූරණය වෙමින්...",
//     backToSummary: "සාරාංශයට ආපසු යන්න",
//     totalAbsent: "මුළු පැමිණ නොමැති සේවකයන්",
//     prepareReport: "ඔබේ වාර්තාව සකස් කරමින්...",
//     generatingSummary: "සාරාංශ වාර්තාව ජනනය කරමින්...",
//     generatingAbsent: "පැමිණ නොමැති සේවක වාර්තාව ජනනය කරමින්...",
//     rosterType: "වර්ගය",
//     roster: "රෝස්ටර්",
//     common: "පොදු",
//     rosterLocations: "රෝස්ටර් ස්ථාන",
//     commonLocations: "පොදු ස්ථාන",
//   },
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const ScrollableContent = styled(Box)(({ theme }) => ({
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "calc(100vh - 260px)",
//     overflowY: "auto",
//   },
// }));

// // Table Styles matching the provided design
// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
// }));

// const EmployeeCard = styled(Box)(({ theme }) => ({
//   padding: "10px 12px",
//   borderRadius: "10px",
//   marginBottom: "8px",
//   boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.07)",
//   backgroundColor: "#fff",
//   border: "1px solid #d1d9e6",
//   transition: "0.15s ease",
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//     transform: "translateY(-1px)",
//     boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "8px 10px",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const RosterAbsentList = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { locationsData, locationsLoading } = useSelector((state) => state.empDiscripency);

//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [language, setLanguage] = useState("en");
//   const [exporting, setExporting] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [rosterSummaryData, setRosterSummaryData] = useState([]);
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [fullAbsentData, setFullAbsentData] = useState([]);
//   const [loadingAbsent, setLoadingAbsent] = useState(false);
//   const [viewMode, setViewMode] = useState("summary");
//   const [rosterTypeFilter, setRosterTypeFilter] = useState("all"); // "all", "roster", "common"
//   const [employees, setEmployees] = useState([]);
//   const [loadingEmployees, setLoadingEmployees] = useState(false);
//   const [selectedRoster, setSelectedRoster] = useState("");
//   const [selectedLocationCode, setSelectedLocationCode] = useState("");

//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   // Helper function to get roster type display label and colors
//   const getRosterTypeDisplay = (rosterValue) => {
//     const roster = (rosterValue || "").trim();
    
//     if (roster === "N") {
//       return {
//         label: t.common || "Common",
//         backgroundColor: "rgba(237, 108, 2, 0.1)",
//         color: "#ed6c02", // Orange
//       };
//     } else if (roster === "A - Scheduled") {
//       return {
//         label: "Roster Scheduled",
//         backgroundColor: "rgba(25, 118, 210, 0.1)",
//         color: "#1976d2", // Blue
//       };
//     } else if (roster === "A - Not Scheduled") {
//       return {
//         label: "Roster Not Scheduled",
//         backgroundColor: "rgba(46, 125, 50, 0.1)",
//         color: "#2e7d32", // Green
//       };
//     }
    
//     // Default fallback
//     return {
//       label: roster || "Unknown",
//       backgroundColor: "rgba(158, 158, 158, 0.1)",
//       color: "#9e9e9e", // Gray
//     };
//   };

//   useEffect(() => {
//     const fetchFullAbsent = async () => {
//       setLoadingAbsent(true);
//       try {
//         const data = await RosterAttendanceService.getFullAbsentDetailsForRoster(formatDate(selectedDate));
//         setFullAbsentData(data || []);
//       } catch (error) {
//         console.error("Error fetching full absent data:", error);
//         setFullAbsentData([]);
//       } finally {
//         setLoadingAbsent(false);
//       }
//     };
//     fetchFullAbsent();
//   }, [selectedDate]);

//   useEffect(() => {
//     // Fetch roster attendance summary for cards with roster type
//     const fetchRosterSummary = async () => {
//       try {
//         const data = await RosterAttendanceService.getByDate(formatDate(selectedDate));
//         setRosterSummaryData(data || []);
//       } catch (error) {
//         console.error("Error fetching roster summary:", error);
//         setRosterSummaryData([]);
//       }
//     };
//     fetchRosterSummary();
//   }, [selectedDate]);

//   useEffect(() => {
//     dispatch(fetchLocations());
//   }, [dispatch]);

//   // Filter data by roster type and group by location + type (uses new API data)
//   const filteredRosterSummaryData = useMemo(() => {
//     if (!fullAbsentData || !Array.isArray(fullAbsentData)) return [];
    
//     // Group by location name AND roster type to keep them separate, SUM duplicates
//     const groupedByLocationAndType = {};
//     fullAbsentData.forEach(item => {
//       if (item.location !== "TOTAL") {
//         const key = `${item.location}__${item.Roster}`;
//         if (!groupedByLocationAndType[key]) {
//           groupedByLocationAndType[key] = {
//             location: item.location,
//             TotalEmp: Number(item.TotalEmp || 0),
//             TodayAbesnt: Number(item.TodayAbesnt || 0),
//             Roster: item.Roster,
//             rosterDisplay: getRosterTypeDisplay(item.Roster),
//           };
//         } else {
//           groupedByLocationAndType[key].TotalEmp += Number(item.TotalEmp || 0);
//           groupedByLocationAndType[key].TodayAbesnt += Number(item.TodayAbesnt || 0);
//         }
//       }
//     });

//     let filtered = Object.values(groupedByLocationAndType).map(item => {
//       // Recalculate absent percentage after aggregation
//       const total = Number(item.TotalEmp || 0);
//       const absent = Number(item.TodayAbesnt || 0);
//       return {
//         ...item,
//         Percentage: total > 0 ? Math.round((absent / total) * 100) : 0,
//       };
//     });

//     if (rosterTypeFilter === "roster") {
//       filtered = filtered.filter(item => item.Roster && item.Roster.startsWith("A"));
//     } else if (rosterTypeFilter === "common") {
//       filtered = filtered.filter(item => item.Roster === "N");
//     }
    
//     return filtered.sort((a, b) => {
//       const absentA = parseInt(a.TodayAbesnt || 0);
//       const absentB = parseInt(b.TodayAbesnt || 0);
//       return absentB - absentA;
//     });
//   }, [fullAbsentData, rosterTypeFilter]);

//   const filteredAbsentData = useMemo(() => {
//     // Map the API response data directly
//     const mappedData = fullAbsentData.map((item) => {
//       // Get the roster type display based on the Roster field from API
//       const rosterDisplay = getRosterTypeDisplay(item.Roster);
      
//       return {
//         ...item,
//         locationName: item.location,
//         departmentName: item.department || item.depatmentCode,
//         rosterType: item.Roster,
//         rosterDisplay: rosterDisplay,
//       };
//     });

//     // Apply location filter
//     let filtered = mappedData;
//     if (selectedLocation) {
//       filtered = filtered.filter((item) => item.location === selectedLocation);
//     }
    
//     // Apply roster type filter
//     if (rosterTypeFilter === "roster") {
//       filtered = filtered.filter(item => item.Roster && item.Roster.startsWith("A"));
//     } else if (rosterTypeFilter === "common") {
//       filtered = filtered.filter(item => item.Roster === "N");
//     }
    
//     return filtered;
//   }, [fullAbsentData, selectedLocation, rosterTypeFilter]);

//   const summaryStats = useMemo(() => {
//     if (!fullAbsentData || !Array.isArray(fullAbsentData) || fullAbsentData.length === 0) {
//       return { 
//         totalStrength: 0, 
//         absentCount: 0, 
//         presentCount: 0, 
//         percentage: 0, 
//         currentLocation: 'All',
//         rosterCount: 0,
//         commonCount: 0,
//         scheduledCount: 0,
//         notScheduledCount: 0,
//       };
//     }

//     // Group by location + Roster type, SUM duplicates
//     const groupedByLocationAndType = {};
//     fullAbsentData.forEach(item => {
//       if (item.location !== "TOTAL") {
//         const key = `${item.location}__${item.Roster}`;
//         if (!groupedByLocationAndType[key]) {
//           groupedByLocationAndType[key] = {
//             location: item.location,
//             TotalEmp: Number(item.TotalEmp || 0),
//             TodayAbesnt: Number(item.TodayAbesnt || 0),
//             Roster: item.Roster,
//           };
//         } else {
//           groupedByLocationAndType[key].TotalEmp += Number(item.TotalEmp || 0);
//           groupedByLocationAndType[key].TodayAbesnt += Number(item.TodayAbesnt || 0);
//         }
//       }
//     });

//     const groupedData = Object.values(groupedByLocationAndType);

//     // Filter by selected location
//     let filteredSummary = selectedLocation
//       ? groupedData.filter((item) => item.location === selectedLocation)
//       : groupedData;

//     // Apply roster type filter
//     if (rosterTypeFilter === "roster") {
//       filteredSummary = filteredSummary.filter(item => item.Roster && item.Roster.startsWith("A"));
//     } else if (rosterTypeFilter === "common") {
//       filteredSummary = filteredSummary.filter(item => item.Roster === "N");
//     }

//     const totalStrength = filteredSummary.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//     const absentCount = filteredSummary.reduce((sum, item) => sum + Number(item.TodayAbesnt || 0), 0);
//     const presentCount = totalStrength - absentCount;
//     const percentage = totalStrength > 0 ? Math.round((absentCount / totalStrength) * 100) : 0;

//     // Count roster types
//     const rosterItems = filteredSummary.filter(item => item.Roster && item.Roster.startsWith("A"));
//     const commonItems = filteredSummary.filter(item => item.Roster === "N");
//     const scheduledItems = filteredSummary.filter(item => item.Roster === "A - Scheduled");
//     const notScheduledItems = filteredSummary.filter(item => item.Roster === "A - Not Scheduled");

//     const currentLocation = selectedLocation || 'All';

//     return { 
//       totalStrength, 
//       presentCount, 
//       absentCount, 
//       percentage, 
//       currentLocation,
//       rosterCount: rosterItems.length,
//       commonCount: commonItems.length,
//       scheduledCount: scheduledItems.length,
//       notScheduledCount: notScheduledItems.length,
//     };
//   }, [fullAbsentData, selectedLocation, rosterTypeFilter]);

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({ open: true, message, severity });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const handleDateChange = (newDate) => {
//     setSelectedDate(newDate);
//     setOpenDatePicker(false);
//     setViewMode("summary");
//     setSelectedLocation("");
//     setSelectedLocationCode("");
//     setRosterTypeFilter("all");
//     setEmployees([]);
//     setSelectedRoster("");
//   };

//   const handleLocationChange = (event) => {
//     const value = event.target.value;
//     setSelectedLocationCode(value);
    
//     // Also set the location name for display
//     const locationObj = locationsData?.find((loc) => loc.locCode === value);
//     setSelectedLocation(locationObj ? locationObj.locName : value);
    
//     if (value === "") {
//       setViewMode("summary");
//       setEmployees([]);
//     } else {
//       setViewMode("details");
//     }
//   };

//   const handleRosterTypeFilterChange = (event) => {
//     setRosterTypeFilter(event.target.value);
//   };

//   const handleLocationCardClick = async (locationName, rosterType) => {
//     if (locationName) {
//       // Find the location CODE from locationsData (API needs code, not name)
//       const locationObj = locationsData?.find(
//         (loc) => loc.locName === locationName || loc.locCode === locationName
//       );
//       const locationCode = locationObj ? locationObj.locCode : locationName;
      
//       console.log("=== CLICK ===");
//       console.log("Location:", locationName, "→ Code:", locationCode, "| Type:", rosterType);
      
//       setSelectedLocation(locationName);
//       setSelectedRoster(rosterType);
//       setViewMode("details");
//       setLoadingEmployees(true);
//       setEmployees([]);
      
//       try {
//         // Use the absent-only endpoint (GetAbsentDetailsForDaywithL)
//         const absentEmployees = await RosterAttendanceService.getAbsentEmployeesByLocation(
//           formatDate(selectedDate),
//           locationCode
//         );
        
//         console.log("Absent employees for", locationCode, ":", absentEmployees.length);
        
//         if (absentEmployees.length > 0) {
//           console.log("Fields:", Object.keys(absentEmployees[0]));
//           console.log("First:", JSON.stringify(absentEmployees[0]));
//         }
        
//         // These are already ABSENT employees only.
//         // Now set them — the API returns absent employees for this location.
//         setEmployees(absentEmployees);
//       } catch (error) {
//         console.error("Error fetching absent employees:", error);
//         setSnackbar({
//           open: true,
//           message: "Failed to load absent employee details",
//           severity: "error",
//         });
//         setEmployees([]);
//       } finally {
//         setLoadingEmployees(false);
//       }
//     }
//   };

//   const handleBackToSummary = () => {
//     setViewMode("summary");
//     setSelectedLocation("");
//     setSelectedLocationCode("");
//     setRosterTypeFilter("all");
//     setEmployees([]);
//     setSelectedRoster("");
//   };

//   const handleExportPDF = async () => {
//     if (exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();

//       setExportProgress(20);

//       // Title based on view mode
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
      
//       if (viewMode === "summary") {
//         doc.text("Roster Attendance Summary Report", pageWidth / 2, 20, { align: "center" });
//       } else {
//         doc.text("Roster Absent Report", pageWidth / 2, 20, { align: "center" });
//       }

//       setExportProgress(30);

//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`Report Date: ${formatDate(selectedDate)}`, 20, 35);
      
//       if (viewMode === "summary") {
//         doc.text(`Total Employees: ${summaryStats.totalStrength}`, 20, 42);
//         doc.text(`Present: ${summaryStats.presentCount}`, 20, 49);
//         doc.text(`Absent: ${summaryStats.absentCount}`, 20, 56);
//         doc.text(`Roster Locations: ${summaryStats.rosterCount}`, 20, 63);
//         doc.text(`Common Locations: ${summaryStats.commonCount}`, 20, 70);
//       } else {
//         doc.text(`Location: ${selectedLocation}`, 20, 42);
//         doc.text(`Total Absent: ${employees.length > 0 ? employees.length : filteredAbsentData.length}`, 20, 49);
//       }
      
//       doc.text(`Generated: ${new Date().toLocaleString()}`, 20, viewMode === "summary" ? 77 : 56);

//       setExportProgress(50);

//       if (viewMode === "summary") {
//         // Summary table with roster type
//         autoTable(doc, {
//           startY: 85,
//           head: [["Location", "Type", "Strength", "Present", "Absent", "Attendance %"]],
//           body: filteredRosterSummaryData?.map((item) => {
//             const total = parseInt(item.TotalEmp || 0);
//             const absent = parseInt(item.TodayAbesnt || 0);
//             const present = total - absent;
//             const percentage = item.Percentage || (total > 0 ? Math.round((present / total) * 100) : 0);
//             const rosterLabel = getRosterTypeDisplay(item.Roster).label;
            
//             return [
//               item.location || "N/A",
//               rosterLabel,
//               total.toString(),
//               present.toString(),
//               absent.toString(),
//               `${percentage}%`,
//             ];
//           }),
//           styles: {
//             fontSize: 9,
//             cellPadding: 4,
//           },
//           headStyles: {
//             fillColor: [21, 76, 121],
//             textColor: 255,
//             fontStyle: "bold",
//           },
//           alternateRowStyles: {
//             fillColor: [245, 248, 252],
//           },
//           foot: [
//             [
//               "TOTAL",
//               `${summaryStats.rosterCount}R/${summaryStats.commonCount}C`,
//               summaryStats.totalStrength.toString(),
//               summaryStats.presentCount.toString(),
//               summaryStats.absentCount.toString(),
//               `${summaryStats.totalStrength > 0 ? Math.round((summaryStats.presentCount / summaryStats.totalStrength) * 100) : 0}%`,
//             ],
//           ],
//           footStyles: {
//             fillColor: [21, 76, 121],
//             textColor: 255,
//             fontStyle: "bold",
//           },
//         });
//       } else if (employees.length > 0) {
//         // Employee-level absent details table
//         autoTable(doc, {
//           startY: 65,
//           head: [["No", "Name", "Department", "Type"]],
//           body: employees?.map((emp, index) => {
//             const empRoster = emp.Roster || emp.roster || emp.rosterType || selectedRoster;
//             const rosterLabel = getRosterTypeDisplay(empRoster).label;
//             return [
//               (index + 1).toString(),
//               emp.name || emp.empName || emp.employeeName || emp.Name || "N/A",
//               emp.department || emp.departmentName || emp.Department || emp.depatmentCode || "N/A",
//               rosterLabel,
//             ];
//           }),
//           styles: {
//             fontSize: 9,
//             cellPadding: 4,
//           },
//           headStyles: {
//             fillColor: [21, 76, 121],
//             textColor: 255,
//             fontStyle: "bold",
//           },
//           alternateRowStyles: {
//             fillColor: [245, 248, 252],
//           },
//           foot: [
//             [
//               "",
//               "Total Absent:",
//               employees.length.toString(),
//               "",
//             ],
//           ],
//           footStyles: {
//             fillColor: [21, 76, 121],
//             textColor: 255,
//             fontStyle: "bold",
//           },
//         });
//       } else {
//         // Absent details table with roster type (location aggregates)
//         autoTable(doc, {
//           startY: 65,
//           head: [["No", "Location", "Strength", "Absent", "Attendance %", "Type"]],
//           body: filteredAbsentData?.map((item, index) => {
//             const total = parseInt(item.TotalEmp || 0);
//             const absent = parseInt(item.TodayAbesnt || 0);
//             const rosterLabel = getRosterTypeDisplay(item.Roster).label;
//             return [
//               (index + 1).toString(),
//               item.location || "N/A",
//               total.toString(),
//               absent.toString(),
//               `${item.Percentage || 0}%`,
//               rosterLabel,
//             ];
//           }),
//           styles: {
//             fontSize: 9,
//             cellPadding: 4,
//           },
//           headStyles: {
//             fillColor: [21, 76, 121],
//             textColor: 255,
//             fontStyle: "bold",
//           },
//           alternateRowStyles: {
//             fillColor: [245, 248, 252],
//           },
//           foot: [
//             [
//               "",
//               "",
//               "Total Absent:",
//               filteredAbsentData.length.toString(),
//               "",
//               "",
//             ],
//           ],
//           footStyles: {
//             fillColor: [21, 76, 121],
//             textColor: 255,
//             fontStyle: "bold",
//           },
//         });
//       }

//       setExportProgress(80);

//       // Add footer
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `Page ${i} of ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//       }

//       setExportProgress(100);

//       // Save the PDF
//       const filename = viewMode === "summary"
//         ? `Roster_Summary_${formatDate(selectedDate)}.pdf`
//         : `Roster_Absent_${formatDate(selectedDate)}.pdf`;
      
//       doc.save(filename);
      
//       showSnackbar("PDF exported successfully!", "success");
      
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("PDF export error:", error);
//       showSnackbar("Failed to export PDF. Please try again.", "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {viewMode === "summary" 
//                 ? t.generatingSummary
//                 : t.generatingAbsent}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: viewMode === "summary" ? "#154c79" : "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% {language === 'si' ? "සම්පූර්ණ" : "Complete"}
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               {t.prepareReport}
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Header with Language Selector */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
//           <Typography
//             sx={{
//               fontSize: "16px",
//               fontWeight: 700,
//               color: "#0e3c66",
//               textAlign: "center",
//               flex: 1,
//             }}
//           >
//             {t.title}
//           </Typography>
          
//           {/* Language Selector */}
//           <Tooltip title={t.language}>
//             <IconButton
//               onClick={handleLanguageMenuOpen}
//               sx={{
//                 backgroundColor: language === 'si' ? '#154c79' : '#f0f0f0',
//                 color: language === 'si' ? 'white' : '#154c79',
//                 '&:hover': {
//                   backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
//                 },
//                 width: '36px',
//                 height: '36px',
//                 ml: 1,
//               }}
//             >
//               {language === 'si' ? (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
//               ) : (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
//               )}
//             </IconButton>
//           </Tooltip>
          
//           <Menu
//             anchorEl={anchorEl}
//             open={Boolean(anchorEl)}
//             onClose={handleLanguageMenuClose}
//             PaperProps={{
//               sx: {
//                 mt: 1,
//                 minWidth: 120,
//                 borderRadius: '8px',
//                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
//               }
//             }}
//           >
//             <MenuItem 
//               onClick={() => handleLanguageChange('en')}
//               selected={language === 'en'}
//               sx={{
//                 backgroundColor: language === 'en' ? 'rgba(21, 76, 121, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(21, 76, 121, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <LanguageIcon sx={{ fontSize: 18, color: '#154c79' }} />
//                 <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
//                   English
//                 </Typography>
//               </Box>
//             </MenuItem>
//             <MenuItem 
//               onClick={() => handleLanguageChange('si')}
//               selected={language === 'si'}
//               sx={{
//                 backgroundColor: language === 'si' ? 'rgba(21, 76, 121, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(21, 76, 121, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <TranslateIcon sx={{ fontSize: 18, color: '#154c79' }} />
//                 <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
//                   සිංහල
//                 </Typography>
//               </Box>
//             </MenuItem>
//           </Menu>
//         </Box>

//         {/* Date Picker, Location Dropdown, Type Filter, and Export Button */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 1.5,
//               minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#154c79",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           <FormControl
//             sx={{
//               flex: isMobile ? 1 : 1.5,
//               minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
//             }}
//             disabled={locationsLoading}
//           >
//             <Select
//               value={selectedLocationCode}
//               onChange={handleLocationChange}
//               displayEmpty
//               disabled={exporting || locationsLoading}
//               sx={{
//                 height: "38px",
//                 borderRadius: "8px",
//                 fontSize: "13px",
//                 fontWeight: "bold",
//                 backgroundColor: exporting ? "#f5f5f5" : "#ffffff",
//                 "& .MuiOutlinedInput-notchedOutline": {
//                   borderColor: exporting ? "#e0e0e0" : undefined,
//                 },
//               }}
//             >
//               <MenuItem value="">
//                 <em>{t.allLocations}</em>
//               </MenuItem>
//               {locationsLoading ? (
//                 <MenuItem disabled>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                     <CircularProgress size={16} />
//                     <Typography variant="body2">{t.loadingLocations}</Typography>
//                   </Box>
//                 </MenuItem>
//               ) : (
//                 locationsData && locationsData.map((location) => (
//                   <MenuItem key={location.locCode} value={location.locCode}>
//                     {location.locName}
//                   </MenuItem>
//                 ))
//               )}
//             </Select>
//           </FormControl>

//           {/* Roster Type Filter Dropdown */}
//           <FormControl
//             sx={{
//               flex: isMobile ? 1 : 1.5,
//               minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
//             }}
//           >
//             <Select
//               value={rosterTypeFilter}
//               onChange={handleRosterTypeFilterChange}
//               displayEmpty
//               disabled={exporting}
//               sx={{
//                 height: "38px",
//                 borderRadius: "8px",
//                 fontSize: "13px",
//                 fontWeight: "bold",
//                 backgroundColor: exporting ? "#f5f5f5" : "#ffffff",
//               }}
//             >
//               <MenuItem value="all">
//                 <em>All Types</em>
//               </MenuItem>
//               <MenuItem value="roster">
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Box sx={{ width: 12, height: 12, backgroundColor: '#1976d2', borderRadius: '2px' }} />
//                   Roster
//                 </Box>
//               </MenuItem>
//               <MenuItem value="common">
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Box sx={{ width: 12, height: 12, backgroundColor: '#ed6c02', borderRadius: '2px' }} />
//                   Common
//                 </Box>
//               </MenuItem>
//             </Select>
//           </FormControl>

//           <Button
//             variant="contained"
//             startIcon={<PictureAsPdfIcon />}
//             onClick={handleExportPDF}
//             disabled={exporting || (viewMode === "details" && employees.length === 0 && (!filteredAbsentData || filteredAbsentData.length === 0))}
//             sx={{
//               flex: isMobile ? 1 : 1,
//               minWidth: isMobile ? "100%" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting 
//                 ? "#9e9e9e" 
//                 : (viewMode === "details" && employees.length === 0 && (!filteredAbsentData || filteredAbsentData.length === 0) 
//                   ? "#bdbdbd" 
//                   : viewMode === "summary" 
//                   ? "#097721" 
//                   : "#2e7d32"),
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (viewMode === "details" && employees.length === 0 && (!filteredAbsentData || filteredAbsentData.length === 0) 
//                     ? "#bdbdbd" 
//                     : viewMode === "summary" 
//                     ? "#0D47A1" 
//                     : "#1b5e20") 
//               },
//             }}
//           >
//             {exporting ? t.exporting : t.exportPdf}
//           </Button>
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             onClose={() => setOpenDatePicker(false)}
//             value={selectedDate}
//             onChange={handleDateChange}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Stats Cards */}
//         {fullAbsentData && fullAbsentData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//                 mb: 1,
//               }}
//             >
//               {/* Total Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography
//                   sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
//                 >
//                   {t.totalStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {summaryStats.totalStrength}
//                 </Typography>
//               </Box>

//               {/* Attendance Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography
//                   sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
//                 >
//                   {t.attendance}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {summaryStats.presentCount}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       summaryStats.totalStrength > 0 
//                         ? (summaryStats.presentCount / summaryStats.totalStrength) > 0.7
//                           ? "#2E7D32"
//                           : (summaryStats.presentCount / summaryStats.totalStrength) > 0.5
//                           ? "#FF9800"
//                           : "#D32F2F"
//                         : "#2E7D32",
//                   }}
//                 >
//                   ({summaryStats.totalStrength > 0 ? Math.round((summaryStats.presentCount / summaryStats.totalStrength) * 100) : 0}%)
//                 </Typography>
//               </Box>

//               {/* Absent Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//                   border: "1px solid #ef9a9a",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#b71c1c",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography
//                   sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
//                 >
//                   {t.absent}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {summaryStats.absentCount}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color: "#D32F2F",
//                   }}
//                 >
//                   ({summaryStats.percentage}%)
//                 </Typography>
//               </Box>
//             </Box>
//           </StickyCardsContainer>
//         )}

//         <ScrollableContent>
//           {viewMode === "summary" ? (
//             // Summary View - TABLE with all locations
//             filteredRosterSummaryData && filteredRosterSummaryData.length > 0 ? (
//               <StyledTableContainer component={Paper} sx={{ mt: 2 }}>
//                 <Table stickyHeader>
//                   <TableHead>
//                     <TableRow>
//                       <StyledTableCell>{t.location}</StyledTableCell>
//                       <StyledTableCell>{t.rosterType}</StyledTableCell>
//                       <StyledTableCell>{t.strength}</StyledTableCell>
//                       <StyledTableCell>{t.present}</StyledTableCell>
//                       <StyledTableCell>{t.absent}</StyledTableCell>
//                       <StyledTableCell>{t.attendanceRate}</StyledTableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {filteredRosterSummaryData.map((row, index) => {
//                       const total = parseInt(row.TotalEmp || 0);
//                       const absent = parseInt(row.TodayAbesnt || 0);
//                       const present = total - absent;
//                       const percentage = row.Percentage || (total > 0 ? Math.round((present / total) * 100) : 0);
                      
//                       return (
//                         <StyledTableRow 
//                           key={index}
//                           onClick={() => handleLocationCardClick(row.location, row.Roster)}
//                         >
//                           <StyledTableCell
//                             sx={{
//                               textAlign: "left",
//                               pl: isMobile ? 2 : 3,
//                               maxWidth: "200px",
//                               overflow: "hidden",
//                               textOverflow: "ellipsis",
//                               whiteSpace: "nowrap",
//                             }}
//                           >
//                             {row.location}
//                           </StyledTableCell>
//                           <StyledTableCell>
//                             <Chip
//                               label={row.rosterDisplay?.label || "Unknown"}
//                               size="small"
//                               sx={{
//                                 backgroundColor: row.rosterDisplay?.backgroundColor || "rgba(158, 158, 158, 0.1)",
//                                 color: row.rosterDisplay?.color || "#9e9e9e",
//                                 fontWeight: 600,
//                                 fontSize: "11px",
//                               }}
//                             />
//                           </StyledTableCell>
//                           <StyledTableCell>{total}</StyledTableCell>
//                           <StyledTableCell>{present}</StyledTableCell>
//                           <StyledTableCell>
//                             <Typography
//                               sx={{
//                                 fontWeight: 700,
//                                 color: absent > 0 ? "#D32F2F" : "#2E7D32",
//                                 fontSize: "13px",
//                               }}
//                             >
//                               {absent}
//                             </Typography>
//                           </StyledTableCell>
//                           <StyledTableCell>
//                             <Typography
//                               sx={{
//                                 fontWeight: 600,
//                                 color: percentage > 70 ? "#2E7D32" : percentage > 50 ? "#FF9800" : "#D32F2F",
//                               }}
//                             >
//                               {percentage}%
//                             </Typography>
//                           </StyledTableCell>
//                         </StyledTableRow>
//                       );
//                     })}
                    
//                     {/* Total Row */}
//                     <StyledTableRow
//                       sx={{
//                         backgroundColor: "#154c79 !important",
//                         "&:hover": { backgroundColor: "#154c79 !important" },
//                         position: "sticky",
//                         bottom: 0,
//                         zIndex: 5,
//                       }}
//                     >
//                       <StyledTableCell
//                         sx={{
//                           color: "#ffffff",
//                           fontWeight: 700,
//                           textAlign: "left",
//                           pl: isMobile ? 2 : 3,
//                         }}
//                       >
//                         {t.total}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {summaryStats.rosterCount}R/{summaryStats.commonCount}C
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {summaryStats.totalStrength}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {summaryStats.presentCount}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {summaryStats.absentCount}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {summaryStats.totalStrength > 0 ? Math.round((summaryStats.presentCount / summaryStats.totalStrength) * 100) : 0}%
//                       </StyledTableCell>
//                     </StyledTableRow>
//                   </TableBody>
//                 </Table>
//               </StyledTableContainer>
//             ) : (
//               <Typography
//                 variant="body1"
//                 sx={{ textAlign: "center", padding: "40px", color: "#666", fontStyle: "italic" }}
//               >
//                 {t.noData}
//               </Typography>
//             )
//           ) : (
//             // Details View - Absent Employees Table
//             <>
//               {viewMode === "details" && (
//                 <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
//                   <Button
//                     variant="text"
//                     startIcon={<ArrowBackIcon />}
//                     onClick={handleBackToSummary}
//                     sx={{
//                       textTransform: "none",
//                       fontWeight: "bold",
//                       color: "#154c79",
//                       fontSize: "13px",
//                     }}
//                   >
//                     {t.backToSummary}
//                   </Button>
//                   <Typography
//                     variant="body2"
//                     sx={{
//                       fontWeight: "bold",
//                       color: "#D32F2F",
//                       fontSize: "13px",
//                     }}
//                   >
//                     {t.totalAbsent}: {employees.length > 0 ? employees.length : filteredAbsentData.length}
//                   </Typography>
//                 </Box>
//               )}

//               {loadingAbsent || loadingEmployees ? (
//                 <Box sx={{ textAlign: "center", padding: "40px" }}>
//                   <CircularProgress sx={{ color: "#154c79" }} />
//                   <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
//                     {t.loading}
//                   </Typography>
//                 </Box>
//               ) : employees.length > 0 ? (
//                 employees.map((emp, index) => (
//                   <EmployeeCard key={emp.empCode || emp.sno || emp.id || index}>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                       {/* Employee Image */}
//                       <img
//                         src={`/api/placeholder/50/50`}
//                         alt={emp.name || emp.empName || "Employee"}
//                         style={{
//                           width: "50px",
//                           height: "50px",
//                           borderRadius: "50%",
//                           objectFit: "cover",
//                           border: "2px solid #154c79",
//                         }}
//                         onError={(e) => (e.target.src = "/placeholder-image.jpg")}
//                       />

//                       {/* Employee Details */}
//                       <Box sx={{ flex: 1 }}>
//                         <Typography
//                           variant="caption"
//                           fontWeight="bold"
//                           sx={{ color: "#154c79", display: "block", fontSize: "11px" }}
//                         >
//                           {emp.sno || emp.empCode || ""}
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           fontWeight="600"
//                           sx={{ fontSize: "14px" }}
//                         >
//                           {emp.name || emp.empName || emp.employeeName || emp.Name || "N/A"}
//                         </Typography>
//                         <Typography
//                           variant="caption"
//                           sx={{ color: "#666", display: "block", fontSize: "11px" }}
//                         >
//                           {selectedLocation || emp.locationCode || emp.locationName || ""}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   </EmployeeCard>
//                 ))
//               ) : (
//                 <Typography
//                   variant="body1"
//                   sx={{ textAlign: "center", padding: "40px", color: "#666", fontStyle: "italic" }}
//                 >
//                   {t.noAbsent}
//                 </Typography>
//               )}
//             </>
//           )}
//         </ScrollableContent>

//         {/* Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={() => setSnackbar({ ...snackbar, open: false })}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={() => setSnackbar({ ...snackbar, open: false })}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAbsentList;


// import React, { useEffect, useState, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchLocations } from "../../actions/EmpDiscripencyAction";
// import RosterAttendanceService from "../../service/RosterAttendanceService";
// import { 
//   Box, 
//   CircularProgress, 
//   Typography, 
//   Paper, 
//   Table, 
//   TableBody, 
//   TableCell, 
//   TableContainer, 
//   TableHead, 
//   TableRow, 
//   useTheme, 
//   useMediaQuery,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Snackbar,
//   Alert,
//   Backdrop,
//   LinearProgress,
//   IconButton,
//   Tooltip,
//   Menu,
//   TextField,
// } from "@mui/material";
// import { styled } from "@mui/material/styles";
// import { format } from "date-fns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import LanguageIcon from "@mui/icons-material/Language";
// import TranslateIcon from "@mui/icons-material/Translate";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Absent List (Roster)",
//     datePicker: "Select Date",
//     exportPdf: "Export PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     attendance: "Attendance",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     loading: "Loading data...",
//     noData: "No attendance data available for the selected date",
//     noAbsent: "No absent employees found for the selected date",
//     total: "Total",
//     reportTitle: "Roster Absent Report",
//     reportDate: "Report Date",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     language: "Language",
//     totalEmp: "Total Employees",
//     present: "Present",
//     attendanceRate: "Attendance Rate (%)",
//     generatingReport: "Generating roster absent report...",
//     allLocations: "All Locations",
//     selectLocation: "Select Location",
//     sno: "S.No",
//     name: "Name",
//     department: "Department",
//     loadingLocations: "Loading locations...",
//   },
//   si: {
//     title: "පැමිණ නොමැති ලැයිස්තුව (රෝස්ටර්)",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     attendance: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "ශක්තිය",
//     loading: "දත්ත පූරණය වෙමින්...",
//     noData: "තෝරාගත් දිනය සඳහා පැමිණීම් දත්ත නොමැත",
//     noAbsent: "තෝරාගත් දිනය සඳහා පැමිණ නොමැති සේවකයන් නොමැත",
//     total: "මුළු",
//     reportTitle: "රෝස්ටර් පැමිණ නොමැති වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     page: "පිටුව",
//     of: "හි",
//     generatedOn: "ජននය කරන ලද දිනය",
//     pdfSuccess: "PDF සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF බාගත කිරීමට නොහැකි විය. නැවත උත්සාහ කරන්න.",
//     language: "භාෂාව",
//     totalEmp: "මුළු සේවකයන්",
//     present: "පැමිණි",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     generatingReport: "රෝස්ටර් පැමිණ නොමැති වාර්තාව ජනනය කරමින්...",
//     allLocations: "සියලුම ස්ථාන",
//     selectLocation: "ස්ථානය තෝරන්න",
//     sno: "අංකය",
//     name: "නම",
//     department: "දෙපාර්තමේන්තුව",
//     loadingLocations: "ස්ථාන පූරණය වෙමින්...",
//   },
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const RosterAbsentList = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { locationsData, locationsLoading } = useSelector((state) => state.empDiscripency);

//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [language, setLanguage] = useState("en");
//   const [exporting, setExporting] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [rosterSummaryData, setRosterSummaryData] = useState([]);
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [fullAbsentData, setFullAbsentData] = useState([]);
//   const [loadingAbsent, setLoadingAbsent] = useState(false);

//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     const fetchFullAbsent = async () => {
//       setLoadingAbsent(true);
//       try {
//         const data = await RosterAttendanceService.getFullAbsentDetailsForRoster(formatDate(selectedDate));
//         setFullAbsentData(data || []);
//       } catch (error) {
//         console.error("Error fetching full absent data:", error);
//         setFullAbsentData([]);
//       } finally {
//         setLoadingAbsent(false);
//       }
//     };
//     fetchFullAbsent();
//   }, [selectedDate]);

//   useEffect(() => {
//     // Fetch roster attendance summary for cards
//     const fetchRosterSummary = async () => {
//       try {
//         const data = await RosterAttendanceService.getByDate(formatDate(selectedDate));
//         setRosterSummaryData(data || []);
//       } catch (error) {
//         console.error("Error fetching roster summary:", error);
//         setRosterSummaryData([]);
//       }
//     };
//     fetchRosterSummary();
//   }, [selectedDate]);

//   useEffect(() => {
//     dispatch(fetchLocations());
//   }, [dispatch]);

//   const filteredData = useMemo(() => {
//     // Map location codes to names and ensure we have proper names
//     const mappedData = fullAbsentData.map((item) => {
//       const locationName = locationsData?.find(loc => loc.locCode === item.locationCode)?.locName || item.locationCode;
//       return {
//         ...item,
//         locationName: locationName,
//         departmentName: item.departmentName || item.depatmentCode // Keep department code if name not available
//       };
//     });

//     if (!selectedLocation) return mappedData;
//     return mappedData.filter((item) => item.locationCode === selectedLocation);
//   }, [fullAbsentData, selectedLocation, locationsData]);

//   const summaryStats = useMemo(() => {
//     if (!rosterSummaryData || !Array.isArray(rosterSummaryData) || rosterSummaryData.length === 0) {
//       return { totalStrength: 0, absentCount: 0, percentage: 0, currentLocation: 'All' };
//     }

//     // Get the location name from the selected location code
//     const locationName = selectedLocation
//       ? locationsData?.find(loc => loc.locCode === selectedLocation)?.locName
//       : null;

//     // Filter roster summary data by location if selected
//     const filteredSummary = locationName
//       ? rosterSummaryData.filter((item) => item.location === locationName)
//       : rosterSummaryData;

//     // Calculate totals from roster attendance summary data (same as RosterAttendance page)
//     const totalStrength = filteredSummary.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//     const presentCount = filteredSummary.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0);
//     const absentCount = totalStrength - presentCount;
//     const percentage = totalStrength > 0 ? Math.round((absentCount / totalStrength) * 100) : 0;

//     // Get current location display name
//     const currentLocation = selectedLocation
//       ? (locationsData?.find(loc => loc.locCode === selectedLocation)?.locName || selectedLocation)
//       : 'All';

//     return { totalStrength, absentCount, percentage, currentLocation };
//   }, [rosterSummaryData, selectedLocation, locationsData]);

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({ open: true, message, severity });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const handleDateChange = (newDate) => {
//     setSelectedDate(newDate);
//     setOpenDatePicker(false);
//   };

//   const handleExportPDF = async () => {
//     if (exporting || !filteredData.length) {
//       if (!filteredData.length) {
//         showSnackbar(t.noAbsent, "warning");
//       }
//       return;
//     }

//     setExporting(true);
//     setExportProgress(10);

//     try {
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();

//       setExportProgress(20);

//       // Title
//       doc.setFontSize(18);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.reportTitle, pageWidth / 2, 20, { align: "center" });

//       // Date info
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${format(selectedDate, "yyyy-MM-dd")}`, 20, 35);
//       doc.text(`${t.generatedOn}: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`, 20, 42);

//       setExportProgress(40);

//       // Summary stats
//       doc.setFontSize(11);
//       doc.setFont("helvetica", "bold");
//       doc.text(`${t.totalStrength}: ${summaryStats.totalStrength}`, 20, 52);
//       doc.text(`${t.location}: ${summaryStats.currentLocation}`, 20, 60);

//       setExportProgress(60);

//       // Table
//       const tableColumns = [t.sno, t.name, t.location, t.department];
//       const tableRows = filteredData.map((item) => [
//         item.sno,
//         item.name,
//         item.locationName,
//         item.departmentName,
//       ]);

//       autoTable(doc, {
//         head: [tableColumns],
//         body: tableRows,
//         startY: 70,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//         margin: { top: 78 },
//         didDrawPage: function (data) {
//           const pageCount = doc.internal.getNumberOfPages();
//           doc.setFontSize(8);
//           doc.text(
//             `${t.page} ${data.pageNumber} ${t.of} ${pageCount}`,
//             pageWidth - 20,
//             pageHeight - 10,
//             { align: "right" }
//           );
//         },
//       });

//       setExportProgress(90);

//       doc.save(`Roster_Absent_${format(selectedDate, "yyyy-MM-dd")}.pdf`);
//       showSnackbar(t.pdfSuccess, "success");

//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
//     } catch (error) {
//       console.error("PDF export error:", error);
//       showSnackbar(t.pdfError, "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };


//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {t.generatingReport}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: "8px",
//                   borderRadius: "4px",
//                   backgroundColor: "rgba(0, 0, 0, 0.1)",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#154c79",
//                   },
//                 }}
//               />
//             </Box>
//             <Typography variant="body2" color="textSecondary">
//               {exportProgress}%
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Title and Language Selector */}
//         <StickyCardsContainer>
//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               mb: 1.5,
//               p: 1,
//             }}
//           >
//             <Typography
//               sx={{
//                 fontSize: "16px",
//                 fontWeight: 700,
//                 color: "#0e3c66",
//                 textAlign: "center",
//                 flex: 1,
//               }}
//             >
//               {t.title}
//             </Typography>

//             {/* Language Selector */}
//             <Tooltip title={t.language}>
//               <IconButton
//                 onClick={handleLanguageMenuOpen}
//                 sx={{
//                   backgroundColor: language === "si" ? "#154c79" : "#f0f0f0",
//                   color: language === "si" ? "white" : "#154c79",
//                   "&:hover": {
//                     backgroundColor: language === "si" ? "#0D47A1" : "#e0e0e0",
//                   },
//                   width: "36px",
//                   height: "36px",
//                   ml: 1,
//                 }}
//               >
//                 {language === "si" ? (
//                   <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
//                     සිං
//                   </Typography>
//                 ) : (
//                   <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
//                     EN
//                   </Typography>
//                 )}
//               </IconButton>
//             </Tooltip>

//             <Menu
//               anchorEl={anchorEl}
//               open={Boolean(anchorEl)}
//               onClose={handleLanguageMenuClose}
//               PaperProps={{
//                 sx: {
//                   mt: 1,
//                   minWidth: 120,
//                   borderRadius: "8px",
//                   boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
//                 },
//               }}
//             >
//               <MenuItem
//                 onClick={() => handleLanguageChange("en")}
//                 selected={language === "en"}
//                 sx={{
//                   backgroundColor:
//                     language === "en" ? "rgba(21, 76, 121, 0.08)" : "transparent",
//                   "&:hover": {
//                     backgroundColor: "rgba(21, 76, 121, 0.12)",
//                   },
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                   <LanguageIcon sx={{ fontSize: 18, color: "#154c79" }} />
//                   <Typography sx={{ fontWeight: language === "en" ? 600 : 400 }}>
//                     English
//                   </Typography>
//                 </Box>
//               </MenuItem>
//               <MenuItem
//                 onClick={() => handleLanguageChange("si")}
//                 selected={language === "si"}
//                 sx={{
//                   backgroundColor:
//                     language === "si" ? "rgba(21, 76, 121, 0.08)" : "transparent",
//                   "&:hover": {
//                     backgroundColor: "rgba(21, 76, 121, 0.12)",
//                   },
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                   <TranslateIcon sx={{ fontSize: 18, color: "#154c79" }} />
//                   <Typography sx={{ fontWeight: language === "si" ? 600 : 400 }}>
//                     සිංහල
//                   </Typography>
//                 </Box>
//               </MenuItem>
//             </Menu>
//           </Box>

//           {/* Date Picker, Location Filter, and Export Button */}
//           <Box
//             sx={{
//               display: "flex",
//               gap: 1,
//               mb: 1.5,
//               alignItems: "center",
//               flexWrap: isMobile ? "wrap" : "nowrap",
//             }}
//           >
//             <Button
//               variant="contained"
//               startIcon={<CalendarTodayIcon />}
//               onClick={() => setOpenDatePicker(true)}
//               disabled={exporting}
//               sx={{
//                 flex: isMobile ? 1 : 2,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//                 },
//               }}
//             >
//               {formatDate(selectedDate)}
//             </Button>

//             {openDatePicker && (
//               <DatePicker
//                 open={openDatePicker}
//                 value={selectedDate}
//                 onChange={handleDateChange}
//                 onClose={() => setOpenDatePicker(false)}
//                 renderInput={(params) => (
//                   <TextField {...params} sx={{ display: "none" }} />
//                 )}
//               />
//             )}

//             <FormControl 
//               sx={{ 
//                 flex: isMobile ? 1 : 2, 
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto" 
//               }}
//               disabled={locationsLoading}
//             >
//               <InputLabel>{t.selectLocation}</InputLabel>
//               <Select
//                 value={selectedLocation}
//                 onChange={(e) => setSelectedLocation(e.target.value)}
//                 label={t.selectLocation}
//                 disabled={exporting || locationsLoading}
//                 sx={{
//                   height: "38px",
//                   borderRadius: "8px",
//                   fontSize: "13px",
//                   fontWeight: "bold",
//                   backgroundColor: exporting ? "#f5f5f5" : "#ffffff",
//                   "& .MuiOutlinedInput-notchedOutline": {
//                     borderColor: exporting ? "#e0e0e0" : undefined,
//                   },
//                 }}
//               >
//                 <MenuItem value="">
//                   <em>{t.allLocations}</em>
//                 </MenuItem>
//                 {locationsLoading ? (
//                   <MenuItem disabled>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                       <CircularProgress size={16} />
//                       <Typography variant="body2">{t.loadingLocations}</Typography>
//                     </Box>
//                   </MenuItem>
//                 ) : (
//                   locationsData && locationsData.map((location) => (
//                     <MenuItem key={location.locCode} value={location.locCode}>
//                       {location.locName}
//                     </MenuItem>
//                   ))
//                 )}
//               </Select>
//             </FormControl>

//             <Tooltip title={exporting ? t.exporting : t.exportPdf}>
//               <span>
//                 <IconButton
//                   onClick={handleExportPDF}
//                   disabled={exporting || !filteredData.length}
//                   sx={{
//                     backgroundColor: "#dc2626",
//                     color: "white",
//                     "&:hover": { backgroundColor: "#b91c1c" },
//                     "&:disabled": { backgroundColor: "#9ca3af", color: "#6b7280" },
//                   }}
//                 >
//                   <PictureAsPdfIcon />
//                 </IconButton>
//               </span>
//             </Tooltip>
//           </Box>

//           {/* Summary Cards */}
//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "center",
//               gap: "6px",
//             }}
//           >
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                 border: "1px solid #aec4e0",
//                 padding: "6px 8px",
//                 borderRadius: "6px",
//                 textAlign: "center",
//                 fontWeight: 600,
//                 color: "#0E3C66",
//                 flex: 1,
//                 minWidth: 0,
//               }}
//             >
//               <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                 Total Strength
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                 {summaryStats.totalStrength}
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//                 border: "1px solid #ef9a9a",
//                 padding: "6px 8px",
//                 borderRadius: "6px",
//                 textAlign: "center",
//                 fontWeight: 600,
//                 color: "#b71c1c",
//                 flex: 1,
//                 minWidth: 0,
//               }}
//             >
//               <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                 {t.absent}
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                 {summaryStats.absentCount}
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 700, color: "#D32F2F" }}>
//                 ({summaryStats.percentage}%)
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                 border: "1px solid #80cbc4",
//                 padding: "6px 8px",
//                 borderRadius: "6px",
//                 textAlign: "center",
//                 fontWeight: 600,
//                 color: "#004d40",
//                 flex: 1,
//                 minWidth: 0,
//               }}
//             >
//               <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                 Location
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                 {summaryStats.currentLocation}
//               </Typography>
//             </Box>
//           </Box>
//         </StickyCardsContainer>

//         {/* Table */}
//         {loadingAbsent ? (
//           <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
//             <CircularProgress sx={{ color: "#154c79" }} />
//             <Typography sx={{ ml: 2, color: "#154c79" }}>{t.loading}</Typography>
//           </Box>
//         ) : filteredData.length > 0 ? (
//           <StyledTableContainer component={Paper}>
//             <Table>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>{t.sno}</StyledTableCell>
//                   <StyledTableCell>{t.name}</StyledTableCell>
//                   <StyledTableCell>{t.location}</StyledTableCell>
//                   <StyledTableCell>{t.department}</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {filteredData.map((item, index) => (
//                   <StyledTableRow key={item.sno || index}>
//                     <StyledTableCell>{item.sno}</StyledTableCell>
//                     <StyledTableCell>{item.name}</StyledTableCell>
//                     <StyledTableCell>{item.locationName}</StyledTableCell>
//                     <StyledTableCell>{item.departmentName}</StyledTableCell>
//                   </StyledTableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         ) : (
//           <Box sx={{ textAlign: "center", py: 4 }}>
//             <Typography sx={{ color: "#666", fontSize: "16px" }}>
//               {t.noAbsent}
//             </Typography>
//           </Box>
//         )}

//         {/* Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={() => setSnackbar({ ...snackbar, open: false })}
//           anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
//         >
//           <Alert
//             onClose={() => setSnackbar({ ...snackbar, open: false })}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAbsentList;



// import React, { useEffect, useState, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchRosterAbsentDetails } from "../../actions/RosterAttendanceAction";
// import { fetchLocations } from "../../actions/EmpDiscripencyAction";
// import RosterAttendanceService from "../../service/RosterAttendanceService";
// import { 
//   Box, 
//   CircularProgress, 
//   Typography, 
//   Paper, 
//   Table, 
//   TableBody, 
//   TableCell, 
//   TableContainer, 
//   TableHead, 
//   TableRow, 
//   useTheme, 
//   useMediaQuery,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Snackbar,
//   Alert,
//   Backdrop,
//   LinearProgress,
//   IconButton,
//   Tooltip,
//   Menu,
//   TextField,
// } from "@mui/material";
// import { styled } from "@mui/material/styles";
// import { format } from "date-fns";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import LanguageIcon from "@mui/icons-material/Language";
// import TranslateIcon from "@mui/icons-material/Translate";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Absent List (Roster)",
//     datePicker: "Select Date",
//     exportPdf: "Export PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     attendance: "Attendance",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     loading: "Loading data...",
//     noData: "No attendance data available for the selected date",
//     noAbsent: "No absent employees found for the selected date",
//     total: "Total",
//     reportTitle: "Roster Absent Report",
//     reportDate: "Report Date",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     language: "Language",
//     totalEmp: "Total Employees",
//     present: "Present",
//     attendanceRate: "Attendance Rate (%)",
//     generatingReport: "Generating roster absent report...",
//     allLocations: "All Locations",
//     selectLocation: "Select Location",
//     sno: "S.No",
//     name: "Name",
//     department: "Department",
//     loadingLocations: "Loading locations...",
//   },
//   si: {
//     title: "පැමිණ නොමැති ලැයිස්තුව (රෝස්ටර්)",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     attendance: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "ශක්තිය",
//     loading: "දත්ත පූරණය වෙමින්...",
//     noData: "තෝරාගත් දිනය සඳහා පැමිණීම් දත්ත නොමැත",
//     noAbsent: "තෝරාගත් දිනය සඳහා පැමිණ නොමැති සේවකයන් නොමැත",
//     total: "මුළු",
//     reportTitle: "රෝස්ටර් පැමිණ නොමැති වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     page: "පිටුව",
//     of: "හි",
//     generatedOn: "ජននය කරන ලද දිනය",
//     pdfSuccess: "PDF සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF බාගත කිරීමට නොහැකි විය. නැවත උත්සාහ කරන්න.",
//     language: "භාෂාව",
//     totalEmp: "මුළු සේවකයන්",
//     present: "පැමිණි",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     generatingReport: "රෝස්ටර් පැමිණ නොමැති වාර්තාව ජනනය කරමින්...",
//     allLocations: "සියලුම ස්ථාන",
//     selectLocation: "ස්ථානය තෝරන්න",
//     sno: "අංකය",
//     name: "නම",
//     department: "දෙපාර්තමේන්තුව",
//     loadingLocations: "ස්ථාන පූරණය වෙමින්...",
//   },
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "500px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const RosterAbsentList = () => {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data: rosterData } = useSelector((state) => state.rosterAttendance);
//   const { locationsData, locationsLoading } = useSelector((state) => state.empDiscripency);

//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [language, setLanguage] = useState("en");
//   const [exporting, setExporting] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const [rosterSummaryData, setRosterSummaryData] = useState([]);
//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [anchorEl, setAnchorEl] = useState(null);

//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchRosterAbsentDetails(formatDate(selectedDate), selectedLocation));
//   }, [dispatch, selectedDate, selectedLocation]);

//   useEffect(() => {
//     // Fetch roster attendance summary for cards
//     const fetchRosterSummary = async () => {
//       try {
//         const data = await RosterAttendanceService.getByDate(formatDate(selectedDate));
//         setRosterSummaryData(data || []);
//       } catch (error) {
//         console.error("Error fetching roster summary:", error);
//         setRosterSummaryData([]);
//       }
//     };
//     fetchRosterSummary();
//   }, [selectedDate]);

//   useEffect(() => {
//     dispatch(fetchLocations());
//   }, [dispatch]);

//   const absentData = useMemo(() => {
//     if (!rosterData || !Array.isArray(rosterData)) return [];
//     return rosterData;
//   }, [rosterData]);

//   const filteredData = useMemo(() => {
//     if (!selectedLocation) return absentData;
//     return absentData.filter((item) => item.locationCode === selectedLocation);
//   }, [absentData, selectedLocation]);

//   const summaryStats = useMemo(() => {
//     if (!rosterSummaryData || !Array.isArray(rosterSummaryData) || rosterSummaryData.length === 0) {
//       return { totalStrength: 0, absentCount: 0, percentage: 0, currentLocation: 'All' };
//     }

//     // Get the location name from the selected location code
//     const locationName = selectedLocation
//       ? locationsData?.find(loc => loc.locCode === selectedLocation)?.locName
//       : null;

//     // Filter roster summary data by location if selected
//     const filteredSummary = locationName
//       ? rosterSummaryData.filter((item) => item.location === locationName)
//       : rosterSummaryData;

//     // Calculate totals from roster attendance summary data (same as RosterAttendance page)
//     const totalStrength = filteredSummary.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0);
//     const presentCount = filteredSummary.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0);
//     const absentCount = totalStrength - presentCount;
//     const percentage = totalStrength > 0 ? Math.round((absentCount / totalStrength) * 100) : 0;

//     // Get current location display name
//     const currentLocation = selectedLocation
//       ? (locationsData?.find(loc => loc.locCode === selectedLocation)?.locName || selectedLocation)
//       : 'All';

//     return { totalStrength, absentCount, percentage, currentLocation };
//   }, [rosterSummaryData, selectedLocation, locationsData]);

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({ open: true, message, severity });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   const handleDateChange = (newDate) => {
//     setSelectedDate(newDate);
//     setOpenDatePicker(false);
//   };

//   const handleExportPDF = async () => {
//     if (exporting || !filteredData.length) {
//       if (!filteredData.length) {
//         showSnackbar(t.noAbsent, "warning");
//       }
//       return;
//     }

//     setExporting(true);
//     setExportProgress(10);

//     try {
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();

//       setExportProgress(20);

//       // Title
//       doc.setFontSize(18);
//       doc.setFont("helvetica", "bold");
//       doc.text(t.reportTitle, pageWidth / 2, 20, { align: "center" });

//       // Date info
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${t.reportDate}: ${format(selectedDate, "yyyy-MM-dd")}`, 20, 35);
//       doc.text(`${t.generatedOn}: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}`, 20, 42);

//       setExportProgress(40);

//       // Summary stats
//       doc.setFontSize(11);
//       doc.setFont("helvetica", "bold");
//       doc.text(`${t.totalStrength}: ${summaryStats.totalAbsent}`, 20, 52);
//       doc.text(`${t.allLocations}: ${summaryStats.uniqueLocations}`, 20, 60);

//       setExportProgress(60);

//       // Table
//       const tableColumns = [t.sno, t.name, t.location, t.department];
//       const tableRows = filteredData.map((item) => [
//         item.sno,
//         item.name,
//         item.locationName || item.locationCode,
//         item.departmentName || item.depatmentCode,
//       ]);

//       autoTable(doc, {
//         head: [tableColumns],
//         body: tableRows,
//         startY: 70,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//         margin: { top: 78 },
//         didDrawPage: function (data) {
//           const pageCount = doc.internal.getNumberOfPages();
//           doc.setFontSize(8);
//           doc.text(
//             `${t.page} ${data.pageNumber} ${t.of} ${pageCount}`,
//             pageWidth - 20,
//             pageHeight - 10,
//             { align: "right" }
//           );
//         },
//       });

//       setExportProgress(90);

//       doc.save(`Roster_Absent_${format(selectedDate, "yyyy-MM-dd")}.pdf`);
//       showSnackbar(t.pdfSuccess, "success");

//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
//     } catch (error) {
//       console.error("PDF export error:", error);
//       showSnackbar(t.pdfError, "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };


//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {t.generatingReport}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: "8px",
//                   borderRadius: "4px",
//                   backgroundColor: "rgba(0, 0, 0, 0.1)",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#154c79",
//                   },
//                 }}
//               />
//             </Box>
//             <Typography variant="body2" color="textSecondary">
//               {exportProgress}%
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Title and Language Selector */}
//         <StickyCardsContainer>
//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               mb: 1.5,
//               p: 1,
//             }}
//           >
//             <Typography
//               sx={{
//                 fontSize: "16px",
//                 fontWeight: 700,
//                 color: "#0e3c66",
//                 textAlign: "center",
//                 flex: 1,
//               }}
//             >
//               {t.title}
//             </Typography>

//             {/* Language Selector */}
//             <Tooltip title={t.language}>
//               <IconButton
//                 onClick={handleLanguageMenuOpen}
//                 sx={{
//                   backgroundColor: language === "si" ? "#154c79" : "#f0f0f0",
//                   color: language === "si" ? "white" : "#154c79",
//                   "&:hover": {
//                     backgroundColor: language === "si" ? "#0D47A1" : "#e0e0e0",
//                   },
//                   width: "36px",
//                   height: "36px",
//                   ml: 1,
//                 }}
//               >
//                 {language === "si" ? (
//                   <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
//                     සිං
//                   </Typography>
//                 ) : (
//                   <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
//                     EN
//                   </Typography>
//                 )}
//               </IconButton>
//             </Tooltip>

//             <Menu
//               anchorEl={anchorEl}
//               open={Boolean(anchorEl)}
//               onClose={handleLanguageMenuClose}
//               PaperProps={{
//                 sx: {
//                   mt: 1,
//                   minWidth: 120,
//                   borderRadius: "8px",
//                   boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
//                 },
//               }}
//             >
//               <MenuItem
//                 onClick={() => handleLanguageChange("en")}
//                 selected={language === "en"}
//                 sx={{
//                   backgroundColor:
//                     language === "en" ? "rgba(21, 76, 121, 0.08)" : "transparent",
//                   "&:hover": {
//                     backgroundColor: "rgba(21, 76, 121, 0.12)",
//                   },
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                   <LanguageIcon sx={{ fontSize: 18, color: "#154c79" }} />
//                   <Typography sx={{ fontWeight: language === "en" ? 600 : 400 }}>
//                     English
//                   </Typography>
//                 </Box>
//               </MenuItem>
//               <MenuItem
//                 onClick={() => handleLanguageChange("si")}
//                 selected={language === "si"}
//                 sx={{
//                   backgroundColor:
//                     language === "si" ? "rgba(21, 76, 121, 0.08)" : "transparent",
//                   "&:hover": {
//                     backgroundColor: "rgba(21, 76, 121, 0.12)",
//                   },
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                   <TranslateIcon sx={{ fontSize: 18, color: "#154c79" }} />
//                   <Typography sx={{ fontWeight: language === "si" ? 600 : 400 }}>
//                     සිංහල
//                   </Typography>
//                 </Box>
//               </MenuItem>
//             </Menu>
//           </Box>

//           {/* Date Picker, Location Filter, and Export Button */}
//           <Box
//             sx={{
//               display: "flex",
//               gap: 1,
//               mb: 1.5,
//               alignItems: "center",
//               flexWrap: isMobile ? "wrap" : "nowrap",
//             }}
//           >
//             <Button
//               variant="contained"
//               startIcon={<CalendarTodayIcon />}
//               onClick={() => setOpenDatePicker(true)}
//               disabled={exporting}
//               sx={{
//                 flex: isMobile ? 1 : 2,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//                 },
//               }}
//             >
//               {formatDate(selectedDate)}
//             </Button>

//             {openDatePicker && (
//               <DatePicker
//                 open={openDatePicker}
//                 value={selectedDate}
//                 onChange={handleDateChange}
//                 onClose={() => setOpenDatePicker(false)}
//                 renderInput={(params) => (
//                   <TextField {...params} sx={{ display: "none" }} />
//                 )}
//               />
//             )}

//             <FormControl 
//               sx={{ 
//                 flex: isMobile ? 1 : 2, 
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto" 
//               }}
//               disabled={locationsLoading}
//             >
//               <InputLabel>{t.selectLocation}</InputLabel>
//               <Select
//                 value={selectedLocation}
//                 onChange={(e) => setSelectedLocation(e.target.value)}
//                 label={t.selectLocation}
//                 disabled={exporting || locationsLoading}
//                 sx={{
//                   height: "38px",
//                   borderRadius: "8px",
//                   fontSize: "13px",
//                   fontWeight: "bold",
//                   backgroundColor: exporting ? "#f5f5f5" : "#ffffff",
//                   "& .MuiOutlinedInput-notchedOutline": {
//                     borderColor: exporting ? "#e0e0e0" : undefined,
//                   },
//                 }}
//               >
//                 <MenuItem value="">
//                   <em>{t.allLocations}</em>
//                 </MenuItem>
//                 {locationsLoading ? (
//                   <MenuItem disabled>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                       <CircularProgress size={16} />
//                       <Typography variant="body2">{t.loadingLocations}</Typography>
//                     </Box>
//                   </MenuItem>
//                 ) : (
//                   locationsData && locationsData.map((location) => (
//                     <MenuItem key={location.locCode} value={location.locCode}>
//                       {location.locName}
//                     </MenuItem>
//                   ))
//                 )}
//               </Select>
//             </FormControl>

//             <Tooltip title={exporting ? t.exporting : t.exportPdf}>
//               <span>
//                 <IconButton
//                   onClick={handleExportPDF}
//                   disabled={exporting || !filteredData.length}
//                   sx={{
//                     backgroundColor: "#dc2626",
//                     color: "white",
//                     "&:hover": { backgroundColor: "#b91c1c" },
//                     "&:disabled": { backgroundColor: "#9ca3af", color: "#6b7280" },
//                   }}
//                 >
//                   <PictureAsPdfIcon />
//                 </IconButton>
//               </span>
//             </Tooltip>
//           </Box>

//           {/* Summary Cards */}
//           <Box
//             sx={{
//               display: "flex",
//               justifyContent: "center",
//               gap: isMobile ? 1 : 2,
//               mb: 2,
//               flexWrap: "wrap",
//             }}
//           >
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                 padding: isMobile ? "12px" : "16px",
//                 borderRadius: "8px",
//                 textAlign: "center",
//                 minWidth: isMobile ? "100px" : "120px",
//                 flex: isMobile ? "1 1 calc(33.33% - 8px)" : "1 1 calc(33.33% - 12px)",
//                 height: isMobile ? "80px" : "100px",
//                 display: "flex",
//                 flexDirection: "column",
//                 justifyContent: "center",
//                 boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
//               }}
//             >
//               <Typography sx={{ fontSize: isMobile ? "11px" : "13px", fontWeight: 500, mb: 1 }}>
//                 Total Strength (Roster)
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "18px" : "24px", fontWeight: 800 }}>
//                 {summaryStats.totalStrength}
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//                 padding: isMobile ? "12px" : "16px",
//                 borderRadius: "8px",
//                 textAlign: "center",
//                 minWidth: isMobile ? "100px" : "120px",
//                 flex: isMobile ? "1 1 calc(33.33% - 8px)" : "1 1 calc(33.33% - 12px)",
//                 height: isMobile ? "80px" : "100px",
//                 display: "flex",
//                 flexDirection: "column",
//                 justifyContent: "center",
//                 boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
//               }}
//             >
//               <Typography sx={{ fontSize: isMobile ? "11px" : "13px", fontWeight: 500, mb: 1 }}>
//                 {t.absent}
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "16px" : "22px", fontWeight: 800 }}>
//                 {summaryStats.absentCount}
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "10px" : "12px", fontWeight: 600, color: "#b71c1c" }}>
//                 ({summaryStats.percentage}%)
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                 padding: isMobile ? "12px" : "16px",
//                 borderRadius: "8px",
//                 textAlign: "center",
//                 minWidth: isMobile ? "100px" : "120px",
//                 flex: isMobile ? "1 1 calc(33.33% - 8px)" : "1 1 calc(33.33% - 12px)",
//                 height: isMobile ? "80px" : "100px",
//                 display: "flex",
//                 flexDirection: "column",
//                 justifyContent: "center",
//                 boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
//               }}
//             >
//               <Typography sx={{ fontSize: isMobile ? "11px" : "13px", fontWeight: 500, mb: 1 }}>
//                 Location
//               </Typography>
//               <Typography sx={{ fontSize: isMobile ? "14px" : "18px", fontWeight: 800 }}>
//                 {summaryStats.currentLocation}
//               </Typography>
//             </Box>
//           </Box>
//         </StickyCardsContainer>

//         {/* Table */}
//         {loading ? (
//           <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
//             <CircularProgress sx={{ color: "#154c79" }} />
//             <Typography sx={{ ml: 2, color: "#154c79" }}>{t.loading}</Typography>
//           </Box>
//         ) : filteredData.length > 0 ? (
//           <StyledTableContainer component={Paper}>
//             <Table>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>{t.sno}</StyledTableCell>
//                   <StyledTableCell>{t.name}</StyledTableCell>
//                   <StyledTableCell>{t.location}</StyledTableCell>
//                   <StyledTableCell>{t.department}</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {filteredData.map((item, index) => (
//                   <StyledTableRow key={item.sno || index}>
//                     <StyledTableCell>{item.sno}</StyledTableCell>
//                     <StyledTableCell>{item.name}</StyledTableCell>
//                     <StyledTableCell>{item.locationName || item.locationCode}</StyledTableCell>
//                     <StyledTableCell>{item.departmentName || item.depatmentCode}</StyledTableCell>
//                   </StyledTableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         ) : (
//           <Box sx={{ textAlign: "center", py: 4 }}>
//             <Typography sx={{ color: "#666", fontSize: "16px" }}>
//               {t.noAbsent}
//             </Typography>
//           </Box>
//         )}

//         {/* Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={() => setSnackbar({ ...snackbar, open: false })}
//           anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
//         >
//           <Alert
//             onClose={() => setSnackbar({ ...snackbar, open: false })}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// };

// export default RosterAbsentList;